import { AlertCircle, Check, ChevronDown, ChevronUp, Download, Home, LoaderCircle, PawPrint, Pill, X } from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import {
  getAccountProfile,
  getOrderFormPrefill,
  getOrderPdfStatus,
  getPreviousPrescriptions,
  getProductDetail,
  requestOrderPdf,
  searchPracticeAddresses,
  submitPrescription,
  type AccountProfile,
  type OrderFormPrefill,
  type Practice,
  type PracticeAddress,
  type ProductDetail,
  type PreviousPrescriptionOption,
  PortalApiError,
  type SubmitPrescriptionResult
} from "../lib/portalApi";
import { appConfig } from "../config/appConfig";
import { isValidUsZipCode, normalizeUsZipCode } from "../lib/usZipCode";

const PDF_READY_RECHECK_DELAYS_MS = [2000, 4000, 7000, 10000, 15000];
const MIN_PATIENT_DOB = "1900-01-01";
const PRICE_PLAN_UNAVAILABLE_MESSAGE = "No price plan information available";

// Current prefill API may report these optional-stage failures with HTTP 200.
// Match its explicit contract, not ordinary empty-result or other warnings.
function isHistoricalPatientLoadFailure(warning: string): boolean {
  return warning === "Companion Pharmacy historical patients could not be loaded. You can enter patient and owner information below."
    || warning === "EMPI reference patient history could not be loaded. You can enter patient and owner information below.";
}

type OrderStep = 1 | 2 | 3;
type CommunicationMethod = "text" | "phone" | "email";
type PatientDemographicsDecision = "update_existing" | "create_new" | "";
type OwnerDetailsDecision = "update_current" | "create_new" | "";
type SubmitValidationError = {
  path?: string;
  field?: string;
  code?: string;
  message?: string;
};

type OrderDraft = {
  practiceId: string;
  patientId: string;
  selectedPatientBaseline: PatientDemographicsSnapshot | null;
  patientDemographicsDecision: PatientDemographicsDecision;
  ownerDetailsDecision: OwnerDetailsDecision;
  patientName: string;
  patientDob: string;
  patientSex: string;
  patientSpecies: string;
  patientWeight: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerPhone: string;
  ownerEmail: string;
  shouldSearchOwnerAddress: boolean;
  ownerAddressSearch: string;
  ownerStreetAddress: string;
  ownerAddressLine2: string;
  ownerCity: string;
  ownerState: string;
  ownerPostalCode: string;
  previousPrescriptionId: string;
  prescriptionProductId: string;
  prescriptionStrength: string;
  prescriptionDosageForm: string;
  prescriptionQuantity: string;
  prescriptionRefills: string;
  prescriptionPrn: boolean;
  prescriptionExpirationDate: string;
  prescriptionDirections: string;
  prescriptionNotes: string;
  communicationMethod: CommunicationMethod;
  orderSignatureBase64: string;
};

function oneYearFromToday(): string {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  return expires.toISOString().slice(0, 10);
}

function todayDateInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

function isValidDateInputValue(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

function isValidPatientDob(value: string): boolean {
  return isValidDateInputValue(value) && value >= MIN_PATIENT_DOB && value <= todayDateInputValue();
}

function prescriptionRefillsSatisfied(draft: Pick<OrderDraft, "prescriptionPrn" | "prescriptionRefills">): boolean {
  return draft.prescriptionPrn || draft.prescriptionRefills.trim().length > 0;
}

function createInitialDraft(): OrderDraft {
  return {
    practiceId: "",
    patientId: "",
    selectedPatientBaseline: null,
    patientDemographicsDecision: "",
    ownerDetailsDecision: "",
    patientName: "",
    patientDob: "",
    patientSex: "",
    patientSpecies: "",
    patientWeight: "",
    ownerFirstName: "",
    ownerLastName: "",
    ownerPhone: "",
    ownerEmail: "",
    shouldSearchOwnerAddress: false,
    ownerAddressSearch: "",
    ownerStreetAddress: "",
    ownerAddressLine2: "",
    ownerCity: "",
    ownerState: "",
    ownerPostalCode: "",
    previousPrescriptionId: "",
    prescriptionProductId: "",
    prescriptionStrength: "",
    prescriptionDosageForm: "",
    prescriptionQuantity: "",
    prescriptionRefills: "",
    prescriptionPrn: false,
    prescriptionExpirationDate: oneYearFromToday(),
    prescriptionDirections: "",
    prescriptionNotes: "",
    communicationMethod: "text",
    orderSignatureBase64: ""
  };
}

function clearPrescriptionProductSelection(draft: OrderDraft): OrderDraft {
  if (
    !draft.previousPrescriptionId &&
    !draft.prescriptionProductId &&
    !draft.prescriptionStrength &&
    !draft.prescriptionDosageForm &&
    !draft.prescriptionQuantity
  ) {
    return draft;
  }

  return {
    ...draft,
    previousPrescriptionId: "",
    prescriptionProductId: "",
    prescriptionStrength: "",
    prescriptionDosageForm: "",
    prescriptionQuantity: ""
  };
}

function clearPrescriptionFieldsForPatientChange(draft: OrderDraft): OrderDraft {
  return {
    ...draft,
    previousPrescriptionId: "",
    prescriptionProductId: "",
    prescriptionStrength: "",
    prescriptionDosageForm: "",
    prescriptionQuantity: "",
    prescriptionRefills: "",
    prescriptionPrn: false,
    prescriptionExpirationDate: oneYearFromToday(),
    prescriptionDirections: "",
    prescriptionNotes: "",
    communicationMethod: "text"
  };
}

type PatientDemographicsSnapshot = Pick<
  OrderDraft,
  | "patientName"
  | "patientDob"
  | "patientSex"
  | "patientSpecies"
  | "patientWeight"
  | "ownerFirstName"
  | "ownerLastName"
  | "ownerPhone"
  | "ownerEmail"
  | "ownerStreetAddress"
  | "ownerAddressLine2"
  | "ownerCity"
  | "ownerState"
  | "ownerPostalCode"
> & {
  patientId?: string;
  source?: string | null;
  sourceLabel?: string | null;
  sourcePatientId?: string | null;
  sourceEvidence?: unknown;
  patientFirstName?: string | null;
  patientLastName?: string | null;
};

type PatientComparableField = keyof Pick<
  OrderDraft,
  | "patientName"
  | "patientDob"
  | "patientSex"
  | "patientSpecies"
  | "patientWeight"
  | "ownerFirstName"
  | "ownerLastName"
  | "ownerPhone"
  | "ownerEmail"
  | "ownerStreetAddress"
  | "ownerAddressLine2"
  | "ownerCity"
  | "ownerState"
  | "ownerPostalCode"
>;

const steps: Array<{
  id: OrderStep;
  title: string;
  summary: string;
  icon: typeof Home;
}> = [
  {
    id: 1,
    title: "Practice",
    summary: "Choose the practice for this order.",
    icon: Home
  },
  {
    id: 2,
    title: "Patient & owner",
    summary: "Select an existing patient or enter new patient and owner details.",
    icon: PawPrint
  },
  {
    id: 3,
    title: "Prescription",
    summary: "Select formulary product, add prescription details, and submit.",
    icon: Pill
  }
];

const sexOptions = [
  { value: "female", label: "Female" },
  { value: "female_spayed", label: "Female Spayed" },
  { value: "male", label: "Male" },
  { value: "male_neutered", label: "Male Neutered" },
  { value: "other", label: "Other" }
];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordManagerIgnoreAttributes = {
  autoComplete: "off",
  "data-1p-ignore": "true",
  "data-op-ignore": "true",
  "data-lpignore": "true",
  "data-bwignore": "true",
  "data-form-type": "other",
  spellCheck: false
} as const;

function formatDateForDisplay(value: string): string {
  const cleaned = value.trim();
  const match = cleaned.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : cleaned;
}

function normalizeDateInputValue(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const cleaned = value?.trim();
    if (!cleaned) continue;

    const isoMatch = cleaned.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    const usMatch = cleaned.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
    if (usMatch) return `${usMatch[3]}-${usMatch[1].padStart(2, "0")}-${usMatch[2].padStart(2, "0")}`;
  }
  return "";
}

function cleanPatientNameFromOption(patient: NonNullable<OrderFormPrefill["patientOptions"][number]>): string {
  const parts = [patient.demographics.firstName, patient.demographics.lastName]
    .map((part) => part?.trim())
    .filter(Boolean);
  if (parts.length) return parts.join(" ");

  const rawName = patient.demographics.name.trim() || patient.label.trim();
  return rawName.split("|")[0]?.trim() || rawName;
}

function patientFirstNameFromOption(patient: NonNullable<OrderFormPrefill["patientOptions"][number]>): string {
  return patient.demographics.firstName?.trim() || cleanPatientNameFromOption(patient).split(/\s+/)[0] || "";
}

function patientWeightLabel(patient: NonNullable<OrderFormPrefill["patientOptions"][number]>): string {
  const formatted = formatWeightForDisplay(String(patient.demographics.weight ?? ""));
  return formatted ? `${formatted} lb` : "";
}

function patientSelectorLabel(patient: NonNullable<OrderFormPrefill["patientOptions"][number]>): string {
  return [
    cleanPatientNameFromOption(patient),
    patient.demographics.sexLabel,
    patient.demographics.dob ? formatDateForDisplay(patient.demographics.dob) : "",
    patient.demographics.speciesLabel,
    patientWeightLabel(patient),
    historicalSourceLabel(patient)
  ].map((part) => part?.trim()).filter(Boolean).join(" | ");
}

function patientOptionMergeKey(patient: NonNullable<OrderFormPrefill["patientOptions"][number]>): string {
  return [
    cleanPatientNameFromOption(patient).toLowerCase(),
    normalizeDateInputValue(patient.demographics.dob, patient.label),
    patient.demographics.sexLabel?.trim().toLowerCase() || patient.demographics.sex?.trim().toLowerCase() || "",
    patient.demographics.speciesLabel?.trim().toLowerCase() || patient.demographics.species?.trim().toLowerCase() || ""
  ].join("|");
}

function patientOptionLooseMergeKey(patient: NonNullable<OrderFormPrefill["patientOptions"][number]>): string {
  const name = cleanPatientNameFromOption(patient);
  const firstName = patient.demographics.firstName?.trim() || name.split(/\s+/)[0] || "";
  return [
    firstName.toLowerCase(),
    normalizeDateInputValue(patient.demographics.dob, patient.label),
    patient.demographics.sexLabel?.trim().toLowerCase() || patient.demographics.sex?.trim().toLowerCase() || "",
    patient.demographics.speciesLabel?.trim().toLowerCase() || patient.demographics.species?.trim().toLowerCase() || ""
  ].join("|");
}

function patientOptionCompletenessScore(patient: NonNullable<OrderFormPrefill["patientOptions"][number]>): number {
  const source = historicalSourceLabel(patient).toLowerCase();
  const demographics = patient.demographics;
  const owner = patient.owner;
  return [
    demographics.firstName,
    demographics.lastName,
    demographics.dob,
    demographics.sexLabel || demographics.sex,
    demographics.speciesLabel || demographics.species,
    demographics.weight,
    owner.firstName,
    owner.lastName,
    owner.phone,
    owner.email,
    owner.address.line1,
    owner.address.city,
    owner.address.state,
    owner.address.postalCode
  ].filter((value) => Boolean(value?.trim())).length +
    (source.includes("companion pharmacy") ? 3 : 0) +
    (source.includes("portal") ? 1 : 0);
}

function mergedPatientSourceLabel(
  a: NonNullable<OrderFormPrefill["patientOptions"][number]>,
  b: NonNullable<OrderFormPrefill["patientOptions"][number]>
): string | null {
  const labels = [historicalSourceLabel(a), historicalSourceLabel(b)];
  const uniqueLabels: string[] = [];
  for (const label of labels) {
    for (const part of label.split(/\s+\+\s+/).map((item) => item.trim()).filter(Boolean)) {
      if (!uniqueLabels.some((existing) => existing.toLowerCase() === part.toLowerCase())) {
        uniqueLabels.push(part);
      }
    }
  }
  return uniqueLabels.length ? uniqueLabels.join(" + ") : null;
}

function firstFilled(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const cleaned = value?.trim();
    if (cleaned) return cleaned;
  }
  return null;
}

function mergePatientOptionSources(
  primary: NonNullable<OrderFormPrefill["patientOptions"][number]>,
  duplicate: NonNullable<OrderFormPrefill["patientOptions"][number]>
): NonNullable<OrderFormPrefill["patientOptions"][number]> {
  return {
    ...primary,
    demographics: {
      ...primary.demographics,
      name: firstFilled(primary.demographics.name, duplicate.demographics.name) ?? "",
      firstName: firstFilled(primary.demographics.firstName, duplicate.demographics.firstName),
      lastName: firstFilled(primary.demographics.lastName, duplicate.demographics.lastName),
      dob: firstFilled(primary.demographics.dob, duplicate.demographics.dob),
      sex: firstFilled(primary.demographics.sex, duplicate.demographics.sex),
      sexLabel: firstFilled(primary.demographics.sexLabel, duplicate.demographics.sexLabel),
      species: firstFilled(primary.demographics.species, duplicate.demographics.species),
      speciesLabel: firstFilled(primary.demographics.speciesLabel, duplicate.demographics.speciesLabel),
      weight: firstFilled(primary.demographics.weight, duplicate.demographics.weight),
      weightUnit: firstFilled(primary.demographics.weightUnit, duplicate.demographics.weightUnit)
    },
    owner: {
      ...primary.owner,
      firstName: firstFilled(primary.owner.firstName, duplicate.owner.firstName),
      lastName: firstFilled(primary.owner.lastName, duplicate.owner.lastName),
      phone: firstFilled(primary.owner.phone, duplicate.owner.phone),
      email: firstFilled(primary.owner.email, duplicate.owner.email),
      address: {
        ...primary.owner.address,
        line1: firstFilled(primary.owner.address?.line1, duplicate.owner.address?.line1),
        line2: firstFilled(primary.owner.address?.line2, duplicate.owner.address?.line2),
        city: firstFilled(primary.owner.address?.city, duplicate.owner.address?.city),
        state: firstFilled(primary.owner.address?.state, duplicate.owner.address?.state),
        postalCode: firstFilled(primary.owner.address?.postalCode, duplicate.owner.address?.postalCode)
      }
    },
    sourceLabel: mergedPatientSourceLabel(primary, duplicate),
    sources: Array.from(new Set([
      ...(primary.sources || []),
      ...(duplicate.sources || [])
    ].filter(Boolean)))
  };
}

function preferPatientOption(
  current: NonNullable<OrderFormPrefill["patientOptions"][number]>,
  incoming: NonNullable<OrderFormPrefill["patientOptions"][number]>
): NonNullable<OrderFormPrefill["patientOptions"][number]> {
  return patientOptionCompletenessScore(incoming) > patientOptionCompletenessScore(current)
    ? mergePatientOptionSources(incoming, current)
    : mergePatientOptionSources(current, incoming);
}

function mergePatientOptions(
  current: OrderFormPrefill["patientOptions"],
  incoming: OrderFormPrefill["patientOptions"]
): OrderFormPrefill["patientOptions"] {
  const byId = new Map(current.map((patient) => [patient.patientId, patient]));
  const byStrictKey = new Map<string, string>();
  const byLooseKey = new Map<string, string>();
  current.forEach((patient) => {
    const strictKey = patientOptionMergeKey(patient);
    const looseKey = patientOptionLooseMergeKey(patient);
    if (strictKey) byStrictKey.set(strictKey, patient.patientId);
    if (looseKey) byLooseKey.set(looseKey, patient.patientId);
  });

  const replacePatient = (
    patientId: string,
    nextPatient: NonNullable<OrderFormPrefill["patientOptions"][number]>
  ) => {
    const stablePatient = { ...nextPatient, patientId };
    byId.set(patientId, stablePatient);
    const strictKey = patientOptionMergeKey(stablePatient);
    const looseKey = patientOptionLooseMergeKey(stablePatient);
    if (strictKey) byStrictKey.set(strictKey, patientId);
    if (looseKey) byLooseKey.set(looseKey, patientId);
  };

  incoming.forEach((patient) => {
    const mergeKey = patientOptionMergeKey(patient);
    const looseMergeKey = patientOptionLooseMergeKey(patient);
    const existingPatientId = byId.has(patient.patientId)
      ? patient.patientId
      : byStrictKey.get(mergeKey) || byLooseKey.get(looseMergeKey);
    if (existingPatientId) {
      const existingPatient = byId.get(existingPatientId);
      replacePatient(existingPatientId, existingPatient ? preferPatientOption(existingPatient, patient) : patient);
      return;
    }
    replacePatient(patient.patientId, patient);
  });

  return Array.from(byId.values());
}

function dedupePatientOptionsForDisplay(patientOptions: OrderFormPrefill["patientOptions"]): OrderFormPrefill["patientOptions"] {
  return mergePatientOptions([], patientOptions);
}

function mergeWarnings(current: string[], incoming: string[]): string[] {
  return [
    ...current,
    ...incoming.filter((warning) => !current.includes(warning))
  ];
}

function historicalSourceLabel(patient: NonNullable<OrderFormPrefill["patientOptions"][number]>): string {
  if (patient.sourceLabel?.trim()) return patient.sourceLabel.trim();
  const source = (patient.source ?? "").toLowerCase();
  if (source.includes("ezyvet") || source.includes("ezy_vet")) return "ezyVet";
  if (source.includes("vetspire")) return "Vetspire";
  if (source.includes("cornerstone")) return "Cornerstone";
  if (source.includes("avimark") || source.includes("avi mark")) return "Avimark";
  if (source.includes("pharmetika")) return "Companion Pharmacy";
  if (source.includes("portal")) return "Portal";
  if (source.includes("pims")) return "PIMS";
  return patient.source?.trim() || "historical";
}

function historicalPracticeAssociation(
  selectedPractice: { name?: string | null } | null | undefined,
  prescription?: PreviousPrescriptionOption
): string {
  return prescription?.practiceAssociation?.trim() || selectedPractice?.name?.trim() || "";
}

function previousPrescriptionSearchText(prescription: PreviousPrescriptionOption): string {
  return [
    prescription.productName,
    prescription.sourceLabel,
    prescription.practiceAssociation,
    prescription.strength,
    prescription.dosageForm,
    prescription.writtenDate
  ].map((part) => part?.trim()).filter(Boolean).join(" ");
}

function previousPrescriptionRefillState(
  prescription: PreviousPrescriptionOption
): Pick<OrderDraft, "prescriptionPrn" | "prescriptionRefills"> {
  const refills = prescription.refills?.trim() ?? "";
  if (/^(prn|as needed)$/i.test(refills)) {
    return {
      prescriptionPrn: true,
      prescriptionRefills: ""
    };
  }

  return {
    prescriptionPrn: false,
    prescriptionRefills: refills.replace(/\D/g, "").slice(0, 2)
  };
}

function cleanPatientNameText(value: string): string {
  const cleaned = value.trim();
  return cleaned.split("|")[0]?.trim() || cleaned;
}

function cleanAddressLine2(value: string | null | undefined): string {
  const cleaned = (value ?? "").trim();
  return /^suite\s*\/\s*apt\s*\/\s*unit$/i.test(cleaned) ? "" : cleaned;
}

const stateOptions = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" }
];

export function OrderFormPage() {
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeStep, setActiveStep] = useState<OrderStep>(1);
  const [draft, setDraft] = useState<OrderDraft>(() => createInitialDraft());
  const [patientValidationAttempt, setPatientValidationAttempt] = useState(0);
  const [stepError, setStepError] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<OrderFormPrefill | null>(null);
  const [isPrefillLoading, setIsPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [isHistoricalPatientsLoading, setIsHistoricalPatientsLoading] = useState(false);
  const [historicalPatientsError, setHistoricalPatientsError] = useState<string | null>(null);
  const historicalPatientsRequestRef = useRef(0);
  const [isProductOptionsLoading, setIsProductOptionsLoading] = useState(false);
  const [productOptionsError, setProductOptionsError] = useState<string | null>(null);
  const [isPatientDecisionOpen, setIsPatientDecisionOpen] = useState(false);
  const [isOwnerDecisionOpen, setIsOwnerDecisionOpen] = useState(false);
  const [isCancelDecisionOpen, setIsCancelDecisionOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<SubmitPrescriptionResult | null>(null);
  const [submitActionMessage, setSubmitActionMessage] = useState<string | null>(null);
  const [submitFieldErrors, setSubmitFieldErrors] = useState<string[]>([]);
  const orderFormTopRef = useRef<HTMLDivElement | null>(null);
  const selectedPracticeIdRef = useRef("");

  function goToStep(step: OrderStep) {
    setActiveStep(step);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }

  function prefillLoadErrorMessage(error: unknown): string {
    if (error instanceof PortalApiError) {
      if (error.isEdgeBlocked) {
        return `Practice details could not be loaded because the request was blocked before it reached the portal. Please try again in a moment. If this continues, contact ${appConfig.support.email}.`;
      }
      if (error.status === 403) {
        return `This practice is not available for prescription submission. Choose another practice or contact ${appConfig.support.email}.`;
      }
      if (error.isRetryable || error.code === "network_error") {
        return "Practice details could not be loaded because the portal could not reach Companion Pharmacy right now. Please try again in a moment.";
      }
    }
    return "Practice details could not be loaded. Choose another practice or try again.";
  }

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    getAccountProfile()
      .then((nextProfile) => {
        setProfile(nextProfile);
        const activePractices = nextProfile.practices.filter((practice) => practice.status === "active");
        if (activePractices.length === 1) {
          setDraft((current) => ({ ...current, practiceId: activePractices[0].id }));
        }
      })
      .catch(() => {
        setProfile(null);
        setLoadError("We could not load your order form right now.");
      })
      .finally(() => setIsLoading(false));
  }, []);

  const selectedPractice = useMemo(
    () => profile?.practices.find((practice) => practice.id === draft.practiceId) ?? null,
    [draft.practiceId, profile?.practices]
  );
  const activePracticeCount = useMemo(
    () => profile?.practices.filter((practice) => practice.status === "active").length ?? 0,
    [profile?.practices]
  );
  const shouldSkipPracticeStep = activePracticeCount === 1;

  useEffect(() => {
    selectedPracticeIdRef.current = selectedPractice?.id ?? "";
  }, [selectedPractice?.id]);

  const formularyReady = Boolean(prefill?.productOptions.length) && !productOptionsError;
  const formularyUnavailableMessage =
    "We couldn’t load this practice’s formulary. Select Retry loading formulary to continue.";
  const formularyGateActive = Boolean(selectedPractice) && (isProductOptionsLoading || !formularyReady);
  const visibleStep: OrderStep = !submitResult && formularyGateActive ? 1 : activeStep;
  const showStepError = Boolean(stepError) && !(
    visibleStep === 1 &&
    Boolean(selectedPractice) &&
    !isProductOptionsLoading &&
    !formularyReady &&
    Boolean(stepError?.toLowerCase().includes("formulary"))
  );

  async function loadProductOptionsForPractice(practiceId: string) {
    setProductOptionsError(null);
    setIsProductOptionsLoading(true);
    try {
      const productPrefill = await getOrderFormPrefill(practiceId, { includePatients: false });
      if (selectedPracticeIdRef.current !== practiceId) return;
      // This response also reloads practice details and species. Retire the
      // earlier prefill failure without clearing unrelated form validation.
      setPrefillError(null);
      setStepError((current) => current === prefillError ? null : current);
      const productOptionsUnavailable = productPrefill.productOptions.length === 0;
      if (productOptionsUnavailable) {
        setProductOptionsError(formularyUnavailableMessage);
      }
      setPrefill((current) => {
        if (!current) return productPrefill;
        return {
          ...current,
          productOptions: productOptionsUnavailable ? current.productOptions : productPrefill.productOptions,
          speciesOptions: current.speciesOptions.length ? current.speciesOptions : productPrefill.speciesOptions,
          warnings: mergeWarnings(current.warnings, productPrefill.warnings)
        };
      });
    } catch (error) {
      if (selectedPracticeIdRef.current === practiceId) setProductOptionsError(prefillLoadErrorMessage(error));
    } finally {
      if (selectedPracticeIdRef.current === practiceId) setIsProductOptionsLoading(false);
    }
  }

  async function loadHistoricalPatients(practiceId: string, stillCurrent: () => boolean = () => true) {
    const requestId = ++historicalPatientsRequestRef.current;
    const isCurrent = () => stillCurrent() && selectedPracticeIdRef.current === practiceId && historicalPatientsRequestRef.current === requestId;
    setHistoricalPatientsError(null);
    setIsHistoricalPatientsLoading(true);
    try {
      const historicalPrefill = await getOrderFormPrefill(practiceId, { includeProducts: false, includePortalPatients: false });
      if (!isCurrent()) return;
      const failureWarning = historicalPrefill.warnings.find(isHistoricalPatientLoadFailure);
      setHistoricalPatientsError(failureWarning ?? null);
      const historicalWarnings = historicalPrefill.warnings.filter((warning) => !isHistoricalPatientLoadFailure(warning));
      setPrefill((current) => current ? {
        ...current,
        patientOptions: mergePatientOptions(current.patientOptions, historicalPrefill.patientOptions),
        speciesOptions: current.speciesOptions.length ? current.speciesOptions : historicalPrefill.speciesOptions,
        warnings: mergeWarnings(current.warnings.filter((warning) => !isHistoricalPatientLoadFailure(warning)), historicalWarnings)
      } : { ...historicalPrefill, warnings: historicalWarnings });
    } catch {
      if (isCurrent()) setHistoricalPatientsError("Companion Pharmacy historical patients could not be loaded. Any patients already listed remain available. You can retry or enter patient and owner information below.");
    } finally {
      if (isCurrent()) setIsHistoricalPatientsLoading(false);
    }
  }

  useEffect(() => {
    if (!selectedPractice || selectedPractice.status !== "active") {
      setPrefill(null);
      setPrefillError(null);
      setIsPrefillLoading(false);
      setIsHistoricalPatientsLoading(false);
      setHistoricalPatientsError(null);
      setProductOptionsError(null);
      setIsProductOptionsLoading(false);
      return;
    }

    let isCurrent = true;
    setPrefill(null);
    setPrefillError(null);
    setProductOptionsError(null);
    setIsPrefillLoading(true);
    setIsHistoricalPatientsLoading(true);
    setHistoricalPatientsError(null);
    setIsProductOptionsLoading(true);

    getOrderFormPrefill(selectedPractice.id, { includeProducts: false, includePharmetikaPatients: false })
      .then((nextPrefill) => {
        if (!isCurrent) return;
        setPrefill((current) => {
          if (!current) return nextPrefill;
          return {
            ...nextPrefill,
            patientOptions: mergePatientOptions(current.patientOptions, nextPrefill.patientOptions),
            productOptions: current.productOptions.length ? current.productOptions : nextPrefill.productOptions,
            speciesOptions: current.speciesOptions.length ? current.speciesOptions : nextPrefill.speciesOptions,
            warnings: mergeWarnings(current.warnings, nextPrefill.warnings)
          };
        });
      })
      .catch((error) => {
        if (isCurrent) setPrefillError(prefillLoadErrorMessage(error));
      })
      .finally(() => {
        if (isCurrent) setIsPrefillLoading(false);
      });

    void loadHistoricalPatients(selectedPractice.id, () => isCurrent);

    loadProductOptionsForPractice(selectedPractice.id);

    return () => {
      isCurrent = false;
      historicalPatientsRequestRef.current += 1;
    };
  }, [selectedPractice?.id, selectedPractice?.status]);

  useEffect(() => {
    if (
      shouldSkipPracticeStep &&
      activeStep === 1 &&
      selectedPractice?.status === "active" &&
      formularyReady &&
      !isProductOptionsLoading &&
      !prefillError
    ) {
      goToStep(2);
    }
  }, [activeStep, formularyReady, isProductOptionsLoading, prefillError, selectedPractice?.status, shouldSkipPracticeStep]);

  useEffect(() => {
    if (!selectedPractice?.id || !formularyGateActive) return;
    if (!isProductOptionsLoading) {
      setDraft((current) => clearPrescriptionProductSelection(current));
      setSubmitFieldErrors((current) => current.filter((path) => path !== "prescription.productId"));
    }
    if (!submitResult && activeStep > 1) {
      goToStep(1);
      setStepError(null);
    }
  }, [activeStep, formularyGateActive, isProductOptionsLoading, selectedPractice?.id, submitResult]);

  function handleContinue() {
    setStepError(null);
    if (activeStep === 1 && !selectedPractice) {
      setStepError("Choose an approved practice before continuing.");
      return;
    }
    if ((activeStep === 1 || activeStep === 2) && selectedPractice) {
      if (isProductOptionsLoading) {
        setStepError("The formulary is still loading. Please wait before continuing.");
        return;
      }
      if (!formularyReady) {
        setStepError(productOptionsError || formularyUnavailableMessage);
        void loadProductOptionsForPractice(selectedPractice.id);
        return;
      }
    }
    if (activeStep === 1 && prefillError) {
      setStepError(prefillError);
      return;
    }
    if (activeStep === 2) {
      setPatientValidationAttempt((attempt) => attempt + 1);
      if (Object.keys(patientOwnerFieldErrors(draft)).length) {
        setStepError("Check the highlighted patient and owner fields before continuing.");
        return;
      }
      if (
        draft.patientId &&
        draft.selectedPatientBaseline &&
        !draft.patientDemographicsDecision &&
        patientDemographicsWereEdited(draft)
      ) {
        setIsPatientDecisionOpen(true);
        return;
      }
      if (
        draft.patientId &&
        draft.selectedPatientBaseline &&
        !draft.ownerDetailsDecision &&
        ownerDetailsWereEdited(draft)
      ) {
        setIsOwnerDecisionOpen(true);
        return;
      }
    }

    if (activeStep < 3) {
      goToStep((activeStep + 1) as OrderStep);
    }
  }

  function handleBack() {
    setStepError(null);
    if (shouldSkipPracticeStep && activeStep === 2) {
      return;
    }
    if (activeStep > 1) {
      goToStep((activeStep - 1) as OrderStep);
    }
  }

  function submitPayload() {
    return {
      practiceId: draft.practiceId,
      patientId: draft.patientId || undefined,
      patient: {
        name: draft.patientName.trim(),
        firstName: draft.patientName.trim(),
        lastName: draft.selectedPatientBaseline?.patientLastName || undefined,
        dob: draft.patientDob,
        sex: draft.patientSex,
        species: draft.patientSpecies,
        weight: draft.patientWeight.trim(),
        weightUnit: "lb"
      },
      owner: {
        firstName: draft.ownerFirstName.trim(),
        lastName: draft.ownerLastName.trim(),
        phone: draft.ownerPhone.trim(),
        email: draft.ownerEmail.trim() || undefined,
        address: {
          line1: draft.ownerStreetAddress.trim(),
          line2: draft.ownerAddressLine2.trim() || undefined,
          city: draft.ownerCity.trim(),
          state: draft.ownerState.trim(),
          postalCode: draft.ownerPostalCode.trim()
        }
      },
      prescription: {
        productId: draft.prescriptionProductId,
        strength: draft.prescriptionStrength.trim(),
        dosageForm: draft.prescriptionDosageForm.trim(),
        quantity: draft.prescriptionQuantity.trim(),
        refills: draft.prescriptionRefills.trim(),
        expirationDate: draft.prescriptionExpirationDate,
        directions: draft.prescriptionDirections.trim(),
        prn: draft.prescriptionPrn,
        notes: draft.prescriptionNotes.trim() || undefined
      },
      communicationPreferences: {
        text: draft.communicationMethod === "text",
        phone: draft.communicationMethod === "phone",
        email: draft.communicationMethod === "email"
      },
      selectedPatientBaseline: draft.selectedPatientBaseline || undefined,
      patientDemographicDecision: draft.patientDemographicsDecision || ("no_change" as const),
      ownerDetailsDecision: draft.ownerDetailsDecision || ("no_change" as const),
      signatureBase64: draft.orderSignatureBase64 || undefined,
      useSavedSignature: profile?.hasSignature === true
    };
  }

  function submitValidationErrors(error: unknown): SubmitValidationError[] {
    const response = error as {
      validation?: { errors?: SubmitValidationError[] };
      payload?: { validation?: { errors?: SubmitValidationError[] } };
    };
    return response.validation?.errors ?? response.payload?.validation?.errors ?? [];
  }

  function firstSubmitError(error: unknown): string {
    const errors = submitValidationErrors(error);
    const fields = errors.map((item) => item.path || item.field).filter((path): path is string => Boolean(path));
    if (fields.length > 0) {
      return errors.find((item) => item.message)?.message || "Order could not be submitted. Review the highlighted fields and try again.";
    }
    if (error instanceof PortalApiError && error.isEdgeBlocked) {
      return `The order request was blocked before it reached the portal. Please try again in a moment. If this continues, contact ${appConfig.support.email}.`;
    }
    if (error instanceof PortalApiError && error.status === 413) {
      return `The order request is too large. Clear the signature and draw it again, then submit once more. If this continues, contact ${appConfig.support.email}.`;
    }
    if (error instanceof PortalApiError && (error.isRetryable || error.code === "network_error")) {
      return `The portal could not reach Companion Pharmacy right now. Please try again in a moment. If this continues, contact ${appConfig.support.email}.`;
    }
    return `Order could not be submitted. Please try again. If this continues, contact ${appConfig.support.email}.`;
  }

  async function handleSubmitOrder() {
    setStepError(null);
    setSubmitFieldErrors([]);
    setSubmitActionMessage(null);
    if (!isValidUsZipCode(draft.ownerPostalCode)) {
      setSubmitFieldErrors(["owner.address.postalCode"]);
      setStepError("Enter a valid owner ZIP code.");
      return;
    }
    if (formularyGateActive && selectedPractice?.id) {
      setStepError(productOptionsError || formularyUnavailableMessage);
      setSubmitFieldErrors(["prescription.productId"]);
      return;
    }
    if (
      !draft.prescriptionProductId ||
      !draft.prescriptionQuantity.trim() ||
      !prescriptionRefillsSatisfied(draft) ||
      !draft.prescriptionDirections.trim() ||
      !draft.prescriptionExpirationDate
    ) {
      setSubmitFieldErrors(
        [
          !draft.prescriptionProductId ? "prescription.productId" : "",
          !draft.prescriptionQuantity.trim() ? "prescription.quantity" : "",
          !prescriptionRefillsSatisfied(draft) ? "prescription.refills" : "",
          !draft.prescriptionDirections.trim() ? "prescription.directions" : "",
          !draft.prescriptionExpirationDate ? "prescription.expirationDate" : ""
        ].filter(Boolean)
      );
      setStepError("Complete product, quantity, refills or PRN, directions, and expiration before submitting.");
      return;
    }
    if (profile?.hasSignature !== true && !draft.orderSignatureBase64) {
      setSubmitFieldErrors(["signature"]);
      setStepError("Draw the required prescriber signature before submitting.");
      return;
    }
    if (!isValidPatientDob(draft.patientDob)) {
      setSubmitFieldErrors(["patient.dob"]);
      setStepError("Enter a valid patient date of birth.");
      goToStep(2);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitPrescription(submitPayload());
      setSubmitResult(result);
      setSubmitActionMessage(null);
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      });
    } catch (error) {
      setSubmitFieldErrors(submitValidationErrors(error).map((item) => item.path || item.field).filter((path): path is string => Boolean(path)));
      setStepError(firstSubmitError(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  function createAnotherPrescriptionForSamePatient() {
    setSubmitResult(null);
    setSubmitActionMessage(null);
    setStepError(null);
    setProductOptionsError(null);
    if (selectedPractice?.id && (!prefill?.productOptions.length || productOptionsError)) {
      void loadProductOptionsForPractice(selectedPractice.id);
    }
    setDraft((current) => ({
      ...current,
      previousPrescriptionId: "",
      prescriptionProductId: "",
      prescriptionStrength: "",
      prescriptionDosageForm: "",
      prescriptionQuantity: "",
      prescriptionRefills: "",
      prescriptionPrn: false,
      prescriptionExpirationDate: oneYearFromToday(),
      prescriptionDirections: "",
      prescriptionNotes: "",
      communicationMethod: "text",
      orderSignatureBase64: ""
    }));
    goToStep(3);
  }

  function viewSubmittedOrder() {
    if (!submitResult?.order.orderId) return;
    window.history.pushState({}, "", `/dashboard?orderId=${encodeURIComponent(submitResult.order.orderId)}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  function cancelPrescription() {
    setIsPatientDecisionOpen(false);
    setIsOwnerDecisionOpen(false);
    setIsCancelDecisionOpen(false);
    setSubmitResult(null);
    setSubmitActionMessage(null);
    setStepError(null);
    setSubmitFieldErrors([]);
    setDraft(createInitialDraft());
    setPatientValidationAttempt(0);
    setPrefill(null);
    setPrefillError(null);
    setProductOptionsError(null);
    window.history.pushState({}, "", "/dashboard");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  async function downloadSubmittedPdf() {
    if (!submitResult?.order.orderId) return;
    setSubmitActionMessage(null);
    const status = submitResult.pdfStatus;
    if (status.available && status.downloadUrl) {
      window.open(status.downloadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    try {
      const orderId = submitResult.order.orderId;
      const queuedStatus = await requestOrderPdf(orderId);
      if (queuedStatus.available && queuedStatus.downloadUrl) {
        setSubmitResult({ ...submitResult, pdfStatus: queuedStatus });
        window.open(queuedStatus.downloadUrl, "_blank", "noopener,noreferrer");
        return;
      }
      setSubmitResult({ ...submitResult, pdfStatus: queuedStatus });
      setSubmitActionMessage("PDF is being prepared. This usually takes a few seconds.");

      for (const delayMs of PDF_READY_RECHECK_DELAYS_MS) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs));
        const nextStatus = await getOrderPdfStatus(orderId);
        setSubmitResult((current) => (current ? { ...current, pdfStatus: nextStatus } : current));
        if (nextStatus.available && nextStatus.downloadUrl) {
          setSubmitActionMessage(null);
          window.open(nextStatus.downloadUrl, "_blank", "noopener,noreferrer");
          return;
        }
      }

      setSubmitActionMessage("PDF is still being prepared. You can try Download PDF again in a moment or open it from the dashboard.");
    } catch {
      setSubmitActionMessage("PDF could not be requested yet. The order was saved, so you can retry from the dashboard.");
    }
  }

  if (isLoading) {
    return (
      <section className="settings-section order-form-card skeleton-block" aria-label="Loading order form">
        <p className="eyebrow">Loading</p>
        <h2>Preparing prescription form</h2>
      </section>
    );
  }

  if (loadError || !profile) {
    return (
      <section className="settings-section account-error" aria-labelledby="order-form-error-title">
        <p className="eyebrow">Order Form</p>
        <h2 id="order-form-error-title">Order form is unavailable</h2>
        <p>{loadError}</p>
        <p className="field-note account-error-note">
          Please refresh and try again. If this continues, contact{" "}
          <a href={`mailto:${appConfig.support.email}`}>{appConfig.support.email}</a>.
        </p>
      </section>
    );
  }

  return (
    <div ref={orderFormTopRef} className="order-form-layout">
      <OrderStepList activeStep={visibleStep} />
      <section className="settings-section order-form-card" aria-labelledby="order-form-step-title">
        {submitResult ? (
          <OrderSubmitSuccessPanel
            result={submitResult}
            actionMessage={submitActionMessage}
            onCreateAnother={createAnotherPrescriptionForSamePatient}
            onDownloadPdf={() => void downloadSubmittedPdf()}
            onViewOrder={viewSubmittedOrder}
          />
        ) : visibleStep === 1 ? (
          <PracticeStep
            historicalPatientsError={historicalPatientsError}
            practices={profile.practices}
            prefill={prefill}
            prefillError={prefillError}
            isHistoricalPatientsLoading={isHistoricalPatientsLoading}
            isPrefillLoading={isPrefillLoading}
            isProductOptionsLoading={isProductOptionsLoading}
            productOptionsError={productOptionsError}
            formularyReady={formularyReady}
            selectedPracticeId={draft.practiceId}
            onCancel={() => setIsCancelDecisionOpen(true)}
            onRetryProductOptions={() => {
              if (selectedPractice?.id) void loadProductOptionsForPractice(selectedPractice.id);
            }}
            onSelect={(practiceId) => {
              setStepError(null);
              goToStep(1);
              setDraft({ ...createInitialDraft(), practiceId });
              setPatientValidationAttempt(0);
            }}
          />
        ) : null}
        {!submitResult && visibleStep === 2 ? (
          <PatientOwnerStep
            validationAttempt={patientValidationAttempt}
            historicalPatientsError={historicalPatientsError}
            onRetryHistoricalPatients={() => {
              if (selectedPractice?.id && !isHistoricalPatientsLoading) void loadHistoricalPatients(selectedPractice.id);
            }}
            isPrefillLoading={isPrefillLoading}
            isHistoricalPatientsLoading={isHistoricalPatientsLoading}
            prefill={prefill}
            prefillError={prefillError}
            draft={draft}
            onCancel={() => setIsCancelDecisionOpen(true)}
            onDraftChange={setDraft}
          />
        ) : null}
        {!submitResult && visibleStep === 3 ? (
          <PrescriptionStep
            draft={draft}
            hasSavedSignature={profile.hasSignature}
            isProductOptionsLoading={isProductOptionsLoading}
            onDraftChange={setDraft}
            prefill={prefill}
            productOptionsError={productOptionsError}
            selectedPractice={selectedPractice}
            submitFieldErrors={submitFieldErrors}
            onCancel={() => setIsCancelDecisionOpen(true)}
            onRetryProductOptions={() => {
              if (selectedPractice?.id) void loadProductOptionsForPractice(selectedPractice.id);
            }}
          />
        ) : null}

        {showStepError ? (
          <div className="form-alert order-step-alert" role="alert">
            <AlertCircle size={18} aria-hidden="true" />
            {stepError}
          </div>
        ) : null}

        {!submitResult ? (
        <div className="order-form-actions">
          <button className="secondary-button" type="button" disabled={visibleStep === 1 || (shouldSkipPracticeStep && visibleStep === 2)} onClick={handleBack}>
            Back
          </button>
          {visibleStep < 3 ? (
            <button className="primary-button" type="button" disabled={formularyGateActive} onClick={handleContinue}>
              Continue
            </button>
          ) : (
            <button className="primary-button" type="button" disabled={isSubmitting || (!profile.hasSignature && !draft.orderSignatureBase64)} onClick={() => void handleSubmitOrder()}>
              {isSubmitting ? "Submitting..." : "Submit prescription"}
            </button>
          )}
        </div>
        ) : null}
      </section>
      <OrderSummaryRail
        activeStep={visibleStep}
        draft={draft}
        isPrefillLoading={isPrefillLoading}
        prefill={prefill}
        selectedPractice={selectedPractice}
      />
      {isPatientDecisionOpen ? (
        <PatientDemographicsDecisionModal
          onClose={() => setIsPatientDecisionOpen(false)}
          onCreateNew={() => {
            const nextDraft = { ...draft, patientDemographicsDecision: "create_new" as const };
            setDraft(nextDraft);
            setIsPatientDecisionOpen(false);
            if (!nextDraft.ownerDetailsDecision && ownerDetailsWereEdited(nextDraft)) {
              setIsOwnerDecisionOpen(true);
            } else {
              goToStep(3);
            }
          }}
          onUpdateExisting={() => {
            const nextDraft = { ...draft, patientDemographicsDecision: "update_existing" as const };
            setDraft(nextDraft);
            setIsPatientDecisionOpen(false);
            if (!nextDraft.ownerDetailsDecision && ownerDetailsWereEdited(nextDraft)) {
              setIsOwnerDecisionOpen(true);
            } else {
              goToStep(3);
            }
          }}
        />
      ) : null}
      {isOwnerDecisionOpen ? (
        <OwnerDetailsDecisionModal
          onClose={() => setIsOwnerDecisionOpen(false)}
          onCreateNew={() => {
            setDraft((current) => ({ ...current, ownerDetailsDecision: "create_new" }));
            setIsOwnerDecisionOpen(false);
            goToStep(3);
          }}
          onUpdateCurrent={() => {
            setDraft((current) => ({ ...current, ownerDetailsDecision: "update_current" }));
            setIsOwnerDecisionOpen(false);
            goToStep(3);
          }}
        />
      ) : null}
      {isCancelDecisionOpen ? (
        <CancelPrescriptionModal onClose={() => setIsCancelDecisionOpen(false)} onConfirm={cancelPrescription} />
      ) : null}
    </div>
  );
}

function patientDemographicsWereEdited(draft: OrderDraft): boolean {
  const baseline = draft.selectedPatientBaseline;
  if (!baseline) return false;
  const keys: PatientComparableField[] = [
    "patientName",
    "patientDob",
    "patientSex",
    "patientSpecies",
    "patientWeight"
  ];
  return keys.some((key) => String(draft[key] ?? "").trim() !== String(baseline[key] ?? "").trim());
}

function ownerDetailsWereEdited(draft: OrderDraft): boolean {
  const baseline = draft.selectedPatientBaseline;
  if (!baseline) return false;
  const keys: PatientComparableField[] = [
    "ownerFirstName",
    "ownerLastName",
    "ownerPhone",
    "ownerEmail",
    "ownerStreetAddress",
    "ownerAddressLine2",
    "ownerCity",
    "ownerState",
    "ownerPostalCode"
  ];
  return keys.some((key) => String(draft[key] ?? "").trim() !== String(baseline[key] ?? "").trim());
}

function displayPatientLabel(label: string, fallbackName: string) {
  const fallback = fallbackName.trim();
  if (fallback) return fallback;
  const cleaned = label
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "")
    .replace(/\b(?:empi|portal|historical|source):[^\s|,;]+/gi, "")
    .replace(/\b[0-9a-f]{24,}\b/gi, "")
    .replace(/\s*[-|,;]\s*$/, "")
    .replace(/^\s*[-|,;]\s*/, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return cleaned || fallbackName.trim() || "Patient";
}

function OrderSubmitSuccessPanel({
  actionMessage,
  onCreateAnother,
  onDownloadPdf,
  onViewOrder,
  result
}: {
  actionMessage: string | null;
  onCreateAnother: () => void;
  onDownloadPdf: () => void;
  onViewOrder: () => void;
  result: SubmitPrescriptionResult;
}) {
  const orderCode = result.order.orderCode || result.order.orderId;

  return (
    <section className="order-submit-success" aria-labelledby="order-submit-success-title">
      <div className="success-icon" aria-hidden="true">
        <Check size={22} />
      </div>
      <div>
        <p className="eyebrow">Submitted</p>
        <h2 id="order-submit-success-title">Prescription submitted</h2>
        <p>
          Order {orderCode} was submitted successfully. The order is available on the dashboard while the pharmacy
          reviews it.
        </p>
        {result.warnings.length ? (
          <div className="context-status warning compact" role="status">
            <AlertCircle size={16} aria-hidden="true" />
            {result.warnings[0]}
          </div>
        ) : null}
        {actionMessage ? (
          <div className="context-status loading compact" role="status">
            <LoaderCircle className="spin" size={16} aria-hidden="true" />
            {actionMessage}
          </div>
        ) : null}
        <div className="order-submit-success-actions">
          <button className="primary-button" type="button" onClick={onCreateAnother}>
            Create another for this patient
          </button>
          <button className="secondary-button" type="button" onClick={onViewOrder}>
            View order
          </button>
          <button className="secondary-button" type="button" onClick={onDownloadPdf}>
            <Download size={16} aria-hidden="true" />
            Download PDF
          </button>
        </div>
      </div>
    </section>
  );
}

function OrderStepList({ activeStep }: { activeStep: OrderStep }) {
  return (
    <nav className="order-stepper" aria-label="Prescription steps">
      {steps.map((step) => {
        const Icon = step.icon;
        const isActive = step.id === activeStep;
        const isComplete = step.id < activeStep;
        return (
          <article className={isActive ? "order-step active" : isComplete ? "order-step complete" : "order-step"} key={step.id}>
            <span className="order-step-index" aria-hidden="true">
              {isComplete ? <Check size={16} /> : <Icon size={16} />}
            </span>
            <div>
              <h2>{step.title}</h2>
              <p>{step.summary}</p>
            </div>
          </article>
        );
      })}
    </nav>
  );
}

function OrderSummaryRail({
  activeStep,
  draft,
  isPrefillLoading,
  prefill,
  selectedPractice
}: {
  activeStep: OrderStep;
  draft: OrderDraft;
  isPrefillLoading: boolean;
  prefill: OrderFormPrefill | null;
  selectedPractice: Practice | null;
}) {
  const selectedProduct = prefill?.productOptions.find((product) => product.productId === draft.prescriptionProductId) ?? null;
  const patientSexLabel = sexOptions.find((option) => option.value === draft.patientSex)?.label;
  const patientSpeciesLabel = prefill?.speciesOptions?.find((option) => option.value === draft.patientSpecies)?.label;
  const ownerName = [draft.ownerFirstName, draft.ownerLastName].map((part) => part.trim()).filter(Boolean).join(" ");
  const patientItems = [
    { label: "Name", value: cleanPatientNameText(draft.patientName) },
    { label: "DOB", value: draft.patientDob ? formatDateForDisplay(draft.patientDob) : "" },
    { label: "Sex", value: patientSexLabel },
    { label: "Species", value: patientSpeciesLabel },
    { label: "Weight", value: draft.patientWeight ? `${draft.patientWeight} lb` : "" }
  ].filter((item) => item.value);
  const ownerItems = [
    { label: "Name", value: ownerName },
    { label: "Phone", value: draft.ownerPhone }
  ].filter((item) => item.value);
  const prescriptionItems =
    activeStep === 3
      ? [
          { label: "Product", value: selectedProduct?.drugName },
          { label: "Strength", value: draft.prescriptionStrength },
          { label: "Form", value: draft.prescriptionDosageForm },
          { label: "Qty", value: draft.prescriptionQuantity },
          { label: "Refills", value: draft.prescriptionRefills },
          { label: "PRN", value: draft.prescriptionPrn ? "Yes" : "" },
          { label: "Expires", value: draft.prescriptionExpirationDate }
        ].filter((item) => item.value)
      : [];

  return (
    <aside className="order-summary-rail" aria-label="Order summary">
      <div className="order-summary-header">
        <p className="eyebrow">Order Summary</p>
      </div>

      <section className="summary-section">
        <h3>Practice</h3>
        {selectedPractice ? <p>{selectedPractice.name}</p> : null}
        {isPrefillLoading ? <span className="summary-hint">Loading practice context...</span> : null}
      </section>

      {patientItems.length ? (
        <section className="summary-section">
          <h3>Patient</h3>
          <dl className="summary-inline-list">
            {patientItems.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      {ownerItems.length ? (
        <section className="summary-section">
          <h3>Owner</h3>
          <dl className="summary-inline-list">
            {ownerItems.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      {prescriptionItems.length ? (
        <section className="summary-section">
          <h3>Prescription</h3>
          <dl className="summary-inline-list">
            {prescriptionItems.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      <p className="summary-active-step">Step {activeStep} of 3</p>
    </aside>
  );
}

function PatientDemographicsDecisionModal({
  onClose,
  onCreateNew,
  onUpdateExisting
}: {
  onClose: () => void;
  onCreateNew: () => void;
  onUpdateExisting: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="demographics-decision-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">Patient demographics</p>
            <h2 id="demographics-decision-title">Save demographic changes?</h2>
          </div>
          <button className="icon-button" type="button" aria-label="Close demographics decision" onClick={onClose}>
            ×
          </button>
        </div>
        <p>
          You edited patient demographic information that was prefilled from a historical patient. Choose whether to update
          the current patient demographics or create a new patient from these values when the prescription is submitted.
        </p>
        <div className="button-row modal-actions">
          <button className="secondary-button" type="button" onClick={onCreateNew}>
            Create new patient
          </button>
          <button className="primary-button" type="button" onClick={onUpdateExisting}>
            Update existing patient
          </button>
        </div>
      </section>
    </div>
  );
}

function OwnerDetailsDecisionModal({
  onClose,
  onCreateNew,
  onUpdateCurrent
}: {
  onClose: () => void;
  onCreateNew: () => void;
  onUpdateCurrent: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="owner-details-decision-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">Owner details</p>
            <h2 id="owner-details-decision-title">Save owner changes?</h2>
          </div>
          <button className="icon-button" type="button" aria-label="Close owner details decision" onClick={onClose}>
            ×
          </button>
        </div>
        <p>
          You edited owner contact or address information that was prefilled from a historical patient. Choose whether to
          update the current owner details or create a new owner for this patient when the prescription is submitted.
        </p>
        <div className="button-row modal-actions">
          <button className="secondary-button" type="button" onClick={onCreateNew}>
            Create new owner
          </button>
          <button className="primary-button" type="button" onClick={onUpdateCurrent}>
            Update current owner
          </button>
        </div>
      </section>
    </div>
  );
}

function CancelPrescriptionModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: () => void }) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="cancel-prescription-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">Cancel prescription</p>
            <h2 id="cancel-prescription-title">Discard this prescription?</h2>
          </div>
          <button className="icon-button" type="button" aria-label="Close cancel confirmation" onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <p>
          This will discard the information entered for this prescription. No order will be submitted.
        </p>
        <div className="button-row modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            Continue editing
          </button>
          <button className="danger-button" type="button" onClick={onConfirm}>
            Discard prescription
          </button>
        </div>
      </section>
    </div>
  );
}

function PracticeStep({
  historicalPatientsError,
  formularyReady,
  isHistoricalPatientsLoading,
  isPrefillLoading,
  isProductOptionsLoading,
  onCancel,
  prefill,
  prefillError,
  productOptionsError,
  practices,
  selectedPracticeId,
  onRetryProductOptions,
  onSelect
}: {
  historicalPatientsError: string | null;
  formularyReady: boolean;
  isHistoricalPatientsLoading: boolean;
  isPrefillLoading: boolean;
  isProductOptionsLoading: boolean;
  onCancel: () => void;
  prefill: OrderFormPrefill | null;
  prefillError: string | null;
  productOptionsError: string | null;
  practices: Practice[];
  selectedPracticeId: string;
  onRetryProductOptions: () => void;
  onSelect: (practiceId: string) => void;
}) {
  const activePractices = practices.filter((practice) => practice.status === "active");
  const unavailablePractices = practices.filter((practice) => practice.status !== "active");

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
  }

  return (
    <form className="order-step-panel" onSubmit={handleSubmit}>
      <div className="section-header">
        <div>
          <p className="eyebrow">Step 1 of 3</p>
          <h2 id="order-form-step-title">Choose the practice for this prescription</h2>
        </div>
        <button className="icon-button order-cancel-button" type="button" aria-label="Cancel prescription" onClick={onCancel}>
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <p className="order-step-copy">
        Practice selection updates the patient list, historical prescriptions, and order routing.
      </p>

      {selectedPracticeId ? (
        <>
          <FormularyContextStatus
            formularyReady={formularyReady}
            isProductOptionsLoading={isProductOptionsLoading}
            productOptionsError={productOptionsError}
            onRetryProductOptions={onRetryProductOptions}
          />
          <OrderContextStatus
            historicalPatientsError={historicalPatientsError}
            formularyReady={formularyReady}
            isProductOptionsLoading={isProductOptionsLoading}
            isHistoricalPatientsLoading={isHistoricalPatientsLoading}
            isLoading={isPrefillLoading}
            prefill={prefill}
            error={prefillError}
          />
        </>
      ) : null}

      {activePractices.length ? (
        <div className="practice-choice-list" role="radiogroup" aria-label="Approved practices">
          {activePractices.map((practice) => (
            <label className={practice.id === selectedPracticeId ? "practice-choice selected" : "practice-choice"} key={practice.id}>
              <input
                type="radio"
                name="practiceId"
                value={practice.id}
                checked={practice.id === selectedPracticeId}
                onChange={() => onSelect(practice.id)}
              />
              <span>
                <strong>
                  {practice.isPrimary ? <Home size={15} aria-hidden="true" /> : null}
                  {practice.name}
                </strong>
              </span>
            </label>
          ))}
        </div>
      ) : (
        <div className="form-alert">
          No approved practices are available for order submission. Contact {appConfig.support.email}.
        </div>
      )}

      {unavailablePractices.length ? (
        <details className="blocked-practices">
          <summary>Practices waiting on approval</summary>
          <div className="blocked-practice-list">
            {unavailablePractices.map((practice) => (
              <article className="blocked-practice" key={practice.id}>
                <strong>{practice.name}</strong>
                <span>{practice.status === "pending" ? "Pending approval" : "Not approved"}</span>
              </article>
            ))}
          </div>
        </details>
      ) : null}
    </form>
  );
}

function FormularyContextStatus({
  formularyReady,
  isProductOptionsLoading,
  onRetryProductOptions,
  productOptionsError
}: {
  formularyReady: boolean;
  isProductOptionsLoading: boolean;
  onRetryProductOptions: () => void;
  productOptionsError: string | null;
}) {
  if (isProductOptionsLoading) {
    return (
      <div className="context-status loading" role="status">
        <LoaderCircle className="spin" size={18} aria-hidden="true" />
        <span>Loading formulary. Patient and owner information will be available when it’s ready.</span>
      </div>
    );
  }

  if (formularyReady && !productOptionsError) {
    return (
      <div className="context-status ready" role="status">
        <Check size={18} aria-hidden="true" />
        <span>Formulary loaded. You can continue to patient and owner information.</span>
      </div>
    );
  }

  return (
    <div className="form-alert order-step-alert formulary-recovery" role="alert">
      <AlertCircle size={18} aria-hidden="true" />
      <div>
        <strong>Formulary unavailable</strong>
        <p>We couldn’t load this practice’s formulary. Retry to continue to patient and owner information. Your patient and owner details stay on this page.</p>
        <button className="secondary-button compact" type="button" onClick={onRetryProductOptions}>
          Retry loading formulary
        </button>
        <p>If retrying doesn’t work, contact <a href={`mailto:${appConfig.support.email}`}>{appConfig.support.email}</a>.</p>
      </div>
    </div>
  );
}

function OrderContextStatus({
  historicalPatientsError,
  error,
  formularyReady,
  isHistoricalPatientsLoading,
  isProductOptionsLoading,
  isLoading,
  prefill
}: {
  historicalPatientsError: string | null;
  error: string | null;
  formularyReady: boolean;
  isHistoricalPatientsLoading: boolean;
  isProductOptionsLoading: boolean;
  isLoading: boolean;
  prefill: OrderFormPrefill | null;
}) {
  if (isLoading) {
    return (
      <div className="context-status loading" role="status">
        <LoaderCircle className="spin" size={18} aria-hidden="true" />
        Loading patients for this practice...
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-alert order-step-alert" role="alert">
        <AlertCircle size={18} aria-hidden="true" />
        {error}
      </div>
    );
  }

  if (!prefill) return null;

  if (isHistoricalPatientsLoading) {
    return (
      <div className="context-status loading" role="status">
        <LoaderCircle className="spin" size={18} aria-hidden="true" />
        <span>Loading historical patients for this practice...</span>
      </div>
    );
  }

  const patientLoadError = historicalPatientsError ?? prefill.warnings.find((warning) =>
    isHistoricalPatientLoadFailure(warning)
    || warning === "Portal-created patient history could not be loaded. You can enter patient and owner information below."
  );
  if (patientLoadError) {
    return (
      <div className="form-alert order-step-alert" role="alert">
        <AlertCircle size={18} aria-hidden="true" />
        <span>{patientLoadError}</span>
      </div>
    );
  }

  // Each patient source reports emptiness independently. Only the combined
  // list, after both requests settle, determines the practice's empty state.
  const visibleWarnings = prefill.warnings.filter((warning) =>
    warning !== "No patient options were returned for the selected practice."
  );

  if (prefill.patientOptions.length === 0) {
    return (
      <div className="context-status warning" role="status">
        <AlertCircle size={18} aria-hidden="true" />
        <span>No patients found for this practice. You can add a new patient in the next step.</span>
      </div>
    );
  }

  return (
    <div className="context-status ready" role="status">
      <Check size={18} aria-hidden="true" />
      <span>
        {formularyReady
          ? "Patient list loaded. You can continue to the next step."
          : isProductOptionsLoading
            ? "Patient list loaded. Waiting for the formulary before continuing."
            : "Patient list loaded. Load the formulary before continuing."}
      </span>
      {visibleWarnings.length ? (
        <ul>
          {visibleWarnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function patientOwnerFieldErrors(draft: OrderDraft): Partial<Record<keyof OrderDraft, string>> {
  const errors: Partial<Record<keyof OrderDraft, string>> = {};
  const required: Array<[keyof OrderDraft, string]> = [
    ["patientName", "Enter the patient name."], ["patientDob", "Enter the date of birth."],
    ["patientSex", "Select the patient's sex."], ["patientSpecies", "Select the species."],
    ["patientWeight", "Enter the weight in pounds."], ["ownerFirstName", "Enter the owner's first name."],
    ["ownerLastName", "Enter the owner's last name."], ["ownerPhone", "Enter the owner's phone number."],
    ["ownerStreetAddress", "Enter the street address."], ["ownerCity", "Enter the city."],
    ["ownerState", "Select the state."], ["ownerPostalCode", "Enter a valid owner ZIP code."]
  ];
  for (const [field, message] of required) if (!String(draft[field] ?? "").trim()) errors[field] = message;
  if (draft.patientDob && !isValidPatientDob(draft.patientDob)) errors.patientDob = "Enter a valid date of birth.";
  if (draft.patientWeight.trim() && !/^\d+(?:\.\d{1,2})?$/.test(draft.patientWeight.trim())) errors.patientWeight = "Enter a weight with up to 2 decimal places.";
  if (draft.ownerPostalCode && !isValidUsZipCode(draft.ownerPostalCode)) errors.ownerPostalCode = "Enter a valid owner ZIP code.";
  if (draft.ownerEmail.trim() && !emailPattern.test(draft.ownerEmail.trim())) errors.ownerEmail = "Enter a valid email address or leave this blank.";
  return errors;
}

function PatientOwnerStep({
  validationAttempt,
  historicalPatientsError,
  onRetryHistoricalPatients,
  draft,
  isHistoricalPatientsLoading,
  isPrefillLoading,
  onCancel,
  onDraftChange,
  prefill,
  prefillError
}: {
  historicalPatientsError: string | null;
  onRetryHistoricalPatients: () => void;
  validationAttempt: number;
  draft: OrderDraft;
  isHistoricalPatientsLoading: boolean;
  isPrefillLoading: boolean;
  onCancel: () => void;
  onDraftChange: (draft: OrderDraft) => void;
  prefill: OrderFormPrefill | null;
  prefillError: string | null;
}) {
  const fieldErrors = validationAttempt ? patientOwnerFieldErrors(draft) : {};
  useEffect(() => {
    if (!validationAttempt) return;
    const errors = patientOwnerFieldErrors(draft);
    const fieldOrder: Array<keyof OrderDraft> = ["patientName", "patientDob", "patientSex", "patientSpecies", "patientWeight", "ownerFirstName", "ownerLastName", "ownerPhone", "ownerEmail", "ownerStreetAddress", "ownerCity", "ownerState", "ownerPostalCode"];
    const firstInvalid = fieldOrder.find((field) => errors[field]);
    if (firstInvalid) document.getElementById("patient-owner-" + firstInvalid)?.focus();
  }, [validationAttempt]);
  function fieldProps(field: keyof OrderDraft) {
    return {
      id: "patient-owner-" + field,
      "aria-invalid": Boolean(fieldErrors[field]),
      "aria-describedby": fieldErrors[field] ? "patient-owner-error-" + field : undefined,
      autoComplete: "off",
      name: `cppp-order-${String(field).replace(/email|phone|address|zip|postal|owner|patient/gi, "field")}`,
      "data-1p-ignore": "true",
      "data-op-ignore": "true",
      "data-lpignore": "true",
      "data-bwignore": "true",
      "data-form-type": "other",
      spellCheck: false,
      className: fieldErrors[field] ? "field-invalid" : undefined
    };
  }
  function fieldError(field: keyof OrderDraft) {
    return fieldErrors[field] ? <span id={"patient-owner-error-" + field} className="field-note field-error-message">{fieldErrors[field]}</span> : null;
  }
  const [ownerAddressSuggestions, setOwnerAddressSuggestions] = useState<PracticeAddress[]>([]);
  const [ownerAddressSearchError, setOwnerAddressSearchError] = useState<string | null>(null);
  const [isOwnerAddressSearching, setIsOwnerAddressSearching] = useState(false);
  const selectedOwnerState = stateOptions.find((state) => state.value === draft.ownerState) ?? null;
  const speciesOptions = prefill?.speciesOptions ?? [];
  const displayPatientOptions = useMemo(
    () => dedupePatientOptionsForDisplay(prefill?.patientOptions ?? []),
    [prefill?.patientOptions]
  );
  const patientOptions = useMemo(
    () =>
      displayPatientOptions.map((patient) => {
        const label = patientSelectorLabel(patient);
        return {
          value: patient.patientId,
          label,
          searchText: `${label} ${patient.source ?? ""} ${patient.demographics.name} ${patient.demographics.dob ?? ""} ${patient.demographics.speciesLabel ?? ""} ${patient.demographics.sexLabel ?? ""} ${patient.demographics.weight ?? ""}`
        };
      }),
    [displayPatientOptions]
  );
  const selectedPatient = displayPatientOptions.find((patient) => patient.patientId === draft.patientId) ?? null;
  const selectedPatientLabel = selectedPatient ? patientSelectorLabel(selectedPatient) : "";

  useEffect(() => {
    setOwnerAddressSuggestions([]);
    setOwnerAddressSearchError(null);
    if (!draft.shouldSearchOwnerAddress || draft.ownerAddressSearch.trim().length < 3) {
      setIsOwnerAddressSearching(false);
      return;
    }

    let isCurrent = true;
    setIsOwnerAddressSearching(true);
    const timeout = window.setTimeout(() => {
      searchPracticeAddresses(draft.ownerAddressSearch.trim(), "owner")
        .then((suggestions) => {
          if (isCurrent) setOwnerAddressSuggestions(suggestions);
        })
        .catch(() => {
          if (isCurrent) {
            setOwnerAddressSuggestions([]);
            setOwnerAddressSearchError("Address search is unavailable. Try again or enter the address below.");
          }
        })
        .finally(() => {
          if (isCurrent) setIsOwnerAddressSearching(false);
        });
    }, 250);

    return () => {
      isCurrent = false;
      window.clearTimeout(timeout);
    };
  }, [draft.ownerAddressSearch, draft.shouldSearchOwnerAddress]);

  function updateDraft(update: Partial<OrderDraft>) {
    onDraftChange({ ...draft, ...update });
  }

  function normalizeOptionValue(
    rawValue: string | null | undefined,
    rawLabel: string | null | undefined,
    options: Array<{ value: string; label: string; sourceCode?: string; sourceLabel?: string }>
  ): string {
    const candidates = [rawValue, rawLabel]
      .map((value) => value?.trim().toLowerCase())
      .filter(Boolean);
    if (!candidates.length) return "";

    const option = options.find((item) => {
      const optionValues = [item.value, item.label, item.sourceCode, item.sourceLabel]
        .map((value) => value?.trim().toLowerCase())
        .filter(Boolean);
      return candidates.some((candidate) => optionValues.includes(candidate));
    });

    return option?.value ?? rawValue?.trim() ?? "";
  }

  function snapshotFromSelectedPatient(patient: NonNullable<typeof selectedPatient>): PatientDemographicsSnapshot {
    const sourcePatientId = patient.patientId.includes(":") ? patient.patientId.split(":").slice(1).join(":") : patient.patientId;
    return {
      patientId: patient.patientId,
      source: patient.source,
      sourceLabel: patient.sourceLabel,
      sourcePatientId,
      sourceEvidence: "sourceEvidence" in patient ? patient.sourceEvidence : undefined,
      patientFirstName: patient.demographics.firstName,
      patientLastName: patient.demographics.lastName,
      patientName: patientFirstNameFromOption(patient),
      patientDob: normalizeDateInputValue(patient.demographics.dob, patient.label, patient.demographics.name),
      patientSex: normalizeOptionValue(patient.demographics.sex, patient.demographics.sexLabel, sexOptions),
      patientSpecies: normalizeOptionValue(patient.demographics.species, patient.demographics.speciesLabel, speciesOptions),
      patientWeight: formatWeightInput(patient.demographics.weight ?? ""),
      ownerFirstName: patient.owner.firstName ?? "",
      ownerLastName: patient.owner.lastName ?? "",
      ownerPhone: formatPhoneInput(patient.owner.phone ?? ""),
      ownerEmail: patient.owner.email ?? "",
      ownerStreetAddress: patient.owner.address?.line1 ?? "",
      ownerAddressLine2: cleanAddressLine2(patient.owner.address?.line2),
      ownerCity: patient.owner.address?.city ?? "",
      ownerState: patient.owner.address?.state ?? "",
      ownerPostalCode: normalizeUsZipCode(patient.owner.address?.postalCode ?? "")
    };
  }

  function handleOwnerAddressSelect(address: PracticeAddress) {
    updateDraft({
      shouldSearchOwnerAddress: false,
      ownerAddressSearch: "",
      ownerStreetAddress: address.line1,
      ownerAddressLine2: cleanAddressLine2(address.line2),
      ownerCity: address.city,
      ownerState: address.state,
      ownerPostalCode: normalizeUsZipCode(address.postalCode)
    });
    setOwnerAddressSuggestions([]);
  }

  function handlePatientSelect(patientId: string) {
    const patient = displayPatientOptions.find((option) => option.patientId === patientId);
    const patientChanged = draft.patientId !== patientId;
    if (!patient) {
      const nextDraft = patientChanged ? clearPrescriptionFieldsForPatientChange(draft) : draft;
      onDraftChange({
        ...nextDraft,
        patientId: "",
        selectedPatientBaseline: null,
        patientDemographicsDecision: "",
        ownerDetailsDecision: ""
      });
      return;
    }
    const snapshot = snapshotFromSelectedPatient(patient);
    const nextDraft = patientChanged ? clearPrescriptionFieldsForPatientChange(draft) : draft;
    onDraftChange({
      ...nextDraft,
      patientId,
      selectedPatientBaseline: snapshot,
      patientDemographicsDecision: "",
      ownerDetailsDecision: "",
      ...snapshot
    });
  }

  function clearSelectedPatient() {
    onDraftChange({
      ...clearPrescriptionFieldsForPatientChange(draft),
      patientId: "",
      selectedPatientBaseline: null,
      patientDemographicsDecision: "",
      ownerDetailsDecision: "",
      patientName: "",
      patientDob: "",
      patientSex: "",
      patientSpecies: "",
      patientWeight: "",
      ownerFirstName: "",
      ownerLastName: "",
      ownerPhone: "",
      ownerEmail: "",
      shouldSearchOwnerAddress: false,
      ownerAddressSearch: "",
      ownerStreetAddress: "",
      ownerAddressLine2: "",
      ownerCity: "",
      ownerState: "",
      ownerPostalCode: ""
    });
    setOwnerAddressSuggestions([]);
  }

  return (
    <section className="order-step-panel" data-1p-ignore="true" data-lpignore="true">
      <div className="section-header">
        <div>
          <p className="eyebrow">Step 2 of 3</p>
          <h2 id="order-form-step-title">Patient and owner</h2>
        </div>
        <button className="icon-button order-cancel-button" type="button" aria-label="Cancel prescription" onClick={onCancel}>
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      {isPrefillLoading ? (
        <div className="context-status loading" role="status">
          <LoaderCircle className="spin" size={18} aria-hidden="true" />
          Loading patients for this practice...
        </div>
      ) : null}
      {!isPrefillLoading && isHistoricalPatientsLoading ? (
        <div className="context-status loading" role="status">
          <LoaderCircle className="spin" size={18} aria-hidden="true" />
          Loading Companion Pharmacy historical patients...
        </div>
      ) : null}
      {prefillError ? (
        <div className="form-alert order-step-alert" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          {prefillError}
        </div>
      ) : null}
      <div className="order-field-group">
        <label>
          Historical patient
          <div className="clearable-select-row">
            <SearchableSelect
              options={patientOptions}
              placeholder={isPrefillLoading || isHistoricalPatientsLoading ? "Loading historical patients..." : "Search historical patients..."}
              value={draft.patientId}
              valueLabel={selectedPatientLabel}
              onChange={handlePatientSelect}
              disabled={isPrefillLoading || Boolean(prefillError) || !prefill || patientOptions.length === 0}
            />
            <button
              className="icon-button clear-select-button"
              type="button"
              aria-label="Clear selected historical patient"
              disabled={!draft.patientId}
              onClick={clearSelectedPatient}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </label>
        {historicalPatientsError ? (
          <div className="context-status warning span-2" role="alert">
            <AlertCircle size={18} aria-hidden="true" />
            <span>{historicalPatientsError}</span>
            <button className="secondary-button" type="button" disabled={isHistoricalPatientsLoading} onClick={onRetryHistoricalPatients}>
              Retry loading historical patients
            </button>
          </div>
        ) : null}
        {!isPrefillLoading && !isHistoricalPatientsLoading && !prefillError && !historicalPatientsError && prefill && !patientOptions.length ? (
          <p className="field-note warning">No historical patients were found for this practice. Enter patient and owner information below.</p>
        ) : null}
        {selectedPatient ? (
          <p className="field-note historical-source-note">
            Source: {historicalSourceLabel(selectedPatient)}
            {historicalPracticeAssociation(prefill?.selectedPractice) ? (
              <> | Practice: {historicalPracticeAssociation(prefill?.selectedPractice)}</>
            ) : null}
          </p>
        ) : null}

          <div className="section-subheader">
            <h3>Patient information</h3>
          </div>
          <div className="order-field-grid">
            <label>
              Patient name *
              <input {...fieldProps("patientName")} value={draft.patientName} onChange={(event) => updateDraft({ patientName: event.target.value })} />
            {fieldError("patientName")}
            </label>
            <label>
              Date of birth *
              <input {...fieldProps("patientDob")}
                type="date"
                min={MIN_PATIENT_DOB}
                max={todayDateInputValue()}
                value={draft.patientDob}
                onChange={(event) => updateDraft({ patientDob: event.target.value })}
              />
            {fieldError("patientDob")}
            </label>
            <label>
              Sex *
              <select {...fieldProps("patientSex")} value={draft.patientSex} onChange={(event) => updateDraft({ patientSex: event.target.value })}>
                <option value="">Select sex</option>
                {sexOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            {fieldError("patientSex")}
            </label>
            <label>
              Species *
              <select {...fieldProps("patientSpecies")}
                value={draft.patientSpecies}
                onChange={(event) => updateDraft({ patientSpecies: event.target.value })}
                disabled={isPrefillLoading || speciesOptions.length === 0}
              >
                <option value="">Select species</option>
                {speciesOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            {fieldError("patientSpecies")}
            </label>
            <label>
              Weight (pounds) *
              <input {...fieldProps("patientWeight")}
                inputMode="decimal"
                value={draft.patientWeight}
                onBlur={() => updateDraft({ patientWeight: formatWeightForDisplay(draft.patientWeight) })}
                onChange={(event) => updateDraft({ patientWeight: formatWeightInput(event.target.value) })}
              />
            {fieldError("patientWeight")}
            </label>
          </div>

          <div className="section-subheader">
            <h3>Owner information</h3>
          </div>
          <div className="order-field-grid">
            <label>
              First name *
              <input {...fieldProps("ownerFirstName")} value={draft.ownerFirstName} onChange={(event) => updateDraft({ ownerFirstName: event.target.value })} />
            {fieldError("ownerFirstName")}
            </label>
            <label>
              Last name *
              <input {...fieldProps("ownerLastName")} value={draft.ownerLastName} onChange={(event) => updateDraft({ ownerLastName: event.target.value })} />
            {fieldError("ownerLastName")}
            </label>
            <label>
              Phone *
              <input {...fieldProps("ownerPhone")}
                inputMode="tel"
                placeholder="(555) 555-5555"
                value={draft.ownerPhone}
                onChange={(event) => updateDraft({ ownerPhone: formatPhoneInput(event.target.value) })}
              />
            {fieldError("ownerPhone")}
            </label>
            <label>
              Email
              <input
                {...fieldProps("ownerEmail")}
                type="text"
                inputMode="email"
                value={draft.ownerEmail}
                onChange={(event) => updateDraft({ ownerEmail: event.target.value })}
              />
            {fieldError("ownerEmail")}
            </label>
          </div>
          <label className="checkbox-row span-2">
            <input
              type="checkbox"
              checked={draft.shouldSearchOwnerAddress}
              onChange={(event) =>
                updateDraft({
                  shouldSearchOwnerAddress: event.target.checked,
                  ownerAddressSearch: event.target.checked ? draft.ownerAddressSearch : ""
                })
              }
            />
            Search for the owner's address
          </label>
          {draft.shouldSearchOwnerAddress ? (
            <div className="address-search-field span-2">
              <label>
                Address search
                <input
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-op-ignore="true"
                  data-lpignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                  spellCheck={false}
                  name="cppp-order-location-search"
                  placeholder="Start typing an address..."
                  value={draft.ownerAddressSearch}
                  onChange={(event) => updateDraft({ ownerAddressSearch: event.target.value })}
                />
              </label>
              {isOwnerAddressSearching ? <p className="field-note">Searching addresses...</p> : null}
              {ownerAddressSearchError ? <p className="field-note" role="alert">{ownerAddressSearchError}</p> : null}
              {!isOwnerAddressSearching && !ownerAddressSearchError && draft.ownerAddressSearch.trim().length >= 3 && ownerAddressSuggestions.length === 0 ? (
                <p className="field-note" role="status">No matching addresses found. Add a city or ZIP code, or enter the address below.</p>
              ) : null}
              {!isOwnerAddressSearching && draft.ownerAddressSearch.trim().length >= 3 && ownerAddressSuggestions.length ? (
                <div className="address-suggestions">
                  {ownerAddressSuggestions.map((address) => (
                    <button
                      className="address-suggestion"
                      type="button"
                      key={address.id ?? address.label}
                      onClick={() => handleOwnerAddressSelect(address)}
                    >
                      {address.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="order-field-grid">
            <label className="span-2">
              Street address *
              <input {...fieldProps("ownerStreetAddress")} value={draft.ownerStreetAddress} onChange={(event) => updateDraft({ ownerStreetAddress: event.target.value })} />
            {fieldError("ownerStreetAddress")}
            </label>
            <label>
              Suite / Apt / Unit
              <input autoComplete="off" data-1p-ignore="true" data-op-ignore="true" data-lpignore="true" data-bwignore="true" data-form-type="other" spellCheck={false} name="cppp-order-location-unit" value={draft.ownerAddressLine2} onChange={(event) => updateDraft({ ownerAddressLine2: cleanAddressLine2(event.target.value) })} />
            </label>
            <label>
              City *
              <input {...fieldProps("ownerCity")} value={draft.ownerCity} onChange={(event) => updateDraft({ ownerCity: event.target.value })} />
            {fieldError("ownerCity")}
            </label>
            <label>
              State *
              <SearchableSelect
                options={stateOptions.map((state) => ({
                  value: state.value,
                  label: `${state.value} - ${state.label}`,
                  searchText: `${state.value} ${state.label}`
                }))}
                id="patient-owner-ownerState"
                invalid={Boolean(fieldErrors.ownerState)}
                describedBy={fieldErrors.ownerState ? "patient-owner-error-ownerState" : undefined}
                placeholder="Search state..."
                value={draft.ownerState}
                valueLabel={selectedOwnerState ? `${selectedOwnerState.value} - ${selectedOwnerState.label}` : ""}
                onChange={(value) => updateDraft({ ownerState: value })}
              />
            {fieldError("ownerState")}
            </label>
            <label>
              Zip code *
              <input {...fieldProps("ownerPostalCode")} inputMode="numeric" maxLength={5} pattern="[0-9]{5}" value={draft.ownerPostalCode} onChange={(event) => updateDraft({ ownerPostalCode: normalizeUsZipCode(event.target.value) })} onPaste={(event) => {
                event.preventDefault();
                const input = event.currentTarget;
                const value = input.value.slice(0, input.selectionStart ?? 0) + event.clipboardData.getData("text") + input.value.slice(input.selectionEnd ?? input.value.length);
                updateDraft({ ownerPostalCode: normalizeUsZipCode(value) });
              }} />
            {fieldError("ownerPostalCode")}
            </label>
          </div>
        </div>
    </section>
  );
}

function PrescriptionStep({
  draft,
  hasSavedSignature,
  isProductOptionsLoading,
  onCancel,
  onDraftChange,
  onRetryProductOptions,
  prefill,
  productOptionsError,
  selectedPractice,
  submitFieldErrors
}: {
  draft: OrderDraft;
  hasSavedSignature: boolean;
  isProductOptionsLoading: boolean;
  onCancel: () => void;
  onDraftChange: (draft: OrderDraft) => void;
  onRetryProductOptions: () => void;
  prefill: OrderFormPrefill | null;
  productOptionsError: string | null;
  selectedPractice: Practice | null;
  submitFieldErrors: string[];
}) {
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingSignatureRef = useRef(false);
  const selectedProduct = prefill?.productOptions.find((product) => product.productId === draft.prescriptionProductId) ?? null;
  const [previousPrescriptions, setPreviousPrescriptions] = useState<PreviousPrescriptionOption[]>([]);
  const [isPreviousPrescriptionsLoading, setIsPreviousPrescriptionsLoading] = useState(false);
  const [previousPrescriptionsError, setPreviousPrescriptionsError] = useState<string | null>(null);
  const [productDetail, setProductDetail] = useState<ProductDetail | null>(null);
  const [isProductDetailLoading, setIsProductDetailLoading] = useState(false);
  const [productDetailError, setProductDetailError] = useState<string | null>(null);
  const [isQuantityMenuOpen, setIsQuantityMenuOpen] = useState(false);
  const quantityOptions = productDetail?.quantityOptions ?? [];
  const selectableQuantityOptions = quantityOptions.filter((option) => !option.isCustomQuantity);
  const pricing = productDetail?.pricing ?? null;
  const quantityValidation = productDetail?.validation?.quantity ?? null;
  const pricingWarning =
    pricing?.message && pricing.message !== quantityValidation?.message
      ? pricing.message
      : null;
  const decimalQuantityAllowed = productDetail?.quantityRules.decimalQuantityAllowed === true;
  const minimumOrderableQuantity = Number(productDetail?.quantityRules.minimumOrderableQuantity ?? "");
  const currentQuantityValue = Number(draft.prescriptionQuantity);
  const currentPricingQuantityValue = Number(pricing?.selectedQuantity);
  const productDetailMatchesDraft =
    productDetail?.productId === draft.prescriptionProductId &&
    Number.isFinite(currentQuantityValue) &&
    currentQuantityValue > 0 &&
    Number.isFinite(currentPricingQuantityValue) &&
    currentPricingQuantityValue === currentQuantityValue;
  const pricingToShow =
    productDetailMatchesDraft && pricing?.estimatedPrice && quantityValidation?.ok !== false ? pricing : null;
  const selectedPreviousPrescription =
    previousPrescriptions.find((option) => option.prescriptionId === draft.previousPrescriptionId) ?? null;
  const hasSubmitError = (path: string) => submitFieldErrors.includes(path);
  const hasSignatureSubmitError = hasSubmitError("signature");

  function updateDraft(update: Partial<OrderDraft>) {
    onDraftChange({ ...draft, ...update });
  }

  function prepareSignatureCanvas() {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    const width = Math.max(Math.floor(rect.width * scale), 1);
    const height = Math.max(Math.floor(rect.height * scale), 1);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const context = canvas.getContext("2d");
    if (!context) return null;
    context.setTransform(scale, 0, 0, scale, 0, 0);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 2.4;
    context.strokeStyle = "#16253f";
    return { canvas, context, rect };
  }

  function signaturePoint(event: ReactPointerEvent<HTMLCanvasElement>) {
    const prepared = prepareSignatureCanvas();
    if (!prepared) return null;
    return {
      context: prepared.context,
      x: event.clientX - prepared.rect.left,
      y: event.clientY - prepared.rect.top
    };
  }

  function startSignature(event: ReactPointerEvent<HTMLCanvasElement>) {
    const point = signaturePoint(event);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    isDrawingSignatureRef.current = true;
    point.context.beginPath();
    point.context.moveTo(point.x, point.y);
  }

  function drawSignature(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!isDrawingSignatureRef.current) return;
    const point = signaturePoint(event);
    if (!point) return;
    point.context.lineTo(point.x, point.y);
    point.context.stroke();
    updateDraft({ orderSignatureBase64: signatureCanvasRef.current?.toDataURL("image/png") ?? "" });
  }

  function stopSignature(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    isDrawingSignatureRef.current = false;
  }

  function clearOrderSignaturePad() {
    const canvas = signatureCanvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      prepareSignatureCanvas();
    }
    isDrawingSignatureRef.current = false;
    updateDraft({ orderSignatureBase64: "" });
  }

  function productById(productId: string) {
    return prefill?.productOptions.find((product) => product.productId === productId) ?? null;
  }

  useEffect(() => {
    if (!selectedPractice || !draft.patientId) {
      setPreviousPrescriptions([]);
      setPreviousPrescriptionsError(null);
      setIsPreviousPrescriptionsLoading(false);
      return;
    }

    let isCurrent = true;
    setIsPreviousPrescriptionsLoading(true);
    setPreviousPrescriptionsError(null);
    getPreviousPrescriptions(selectedPractice.id, draft.patientId)
      .then((result) => {
        if (isCurrent) setPreviousPrescriptions(result.options);
      })
      .catch(() => {
        if (isCurrent) {
          setPreviousPrescriptions([]);
          setPreviousPrescriptionsError(null);
        }
      })
      .finally(() => {
        if (isCurrent) setIsPreviousPrescriptionsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [draft.patientId, selectedPractice]);

  useEffect(() => {
    if (!selectedPractice || !draft.prescriptionProductId) {
      setProductDetail(null);
      setProductDetailError(null);
      setIsProductDetailLoading(false);
      return;
    }

    let isCurrent = true;
    const abortController = new AbortController();
    const requestedProductId = draft.prescriptionProductId;
    const requestedQuantity = draft.prescriptionQuantity || undefined;
    setIsProductDetailLoading(true);
    setProductDetailError(null);
    const timeout = window.setTimeout(() => {
      getProductDetail(selectedPractice.id, requestedProductId, requestedQuantity, {
        signal: abortController.signal
      })
        .then((detail) => {
          if (isCurrent && detail.productId === requestedProductId) {
            setProductDetail({
              ...detail,
              quantityOptions: detail.quantityOptions ?? [],
              pricing: detail.pricing ?? {
                selectedQuantity: draft.prescriptionQuantity || null,
                selectedPricePlanEntryId: null,
                unitPrice: null,
                additionalFee: null,
                unitPercentage: null,
                minimumPrice: null,
                minimumPriceApplied: false,
                estimatedPrice: null,
                message: PRICE_PLAN_UNAVAILABLE_MESSAGE
              }
            });
          }
        })
        .catch((error) => {
          if (error instanceof DOMException && error.name === "AbortError") return;
          if (isCurrent) {
            setProductDetail(null);
            setProductDetailError(PRICE_PLAN_UNAVAILABLE_MESSAGE);
          }
        })
        .finally(() => {
          if (isCurrent) setIsProductDetailLoading(false);
        });
    }, draft.prescriptionQuantity ? 250 : 0);

    return () => {
      isCurrent = false;
      abortController.abort();
      window.clearTimeout(timeout);
    };
  }, [draft.prescriptionProductId, draft.prescriptionQuantity, selectedPractice]);

  function productIdFromPreviousPrescription(previousPrescription: PreviousPrescriptionOption): string {
    const identifiers = [
      previousPrescription.reportProductListId,
      previousPrescription.productId,
      previousPrescription.pharmacySystemProductId,
      previousPrescription.ndc
    ].map((value) => value?.trim().toLowerCase()).filter(Boolean);
    const exactMatch = prefill?.productOptions.find((product) =>
      identifiers.includes(product.productId.trim().toLowerCase())
    );
    if (exactMatch) return exactMatch.productId;

    const previousName = previousPrescription.productName.trim().toLowerCase();
    const nameMatch = prefill?.productOptions.find((product) =>
      product.label.trim().toLowerCase() === previousName ||
      product.drugName.trim().toLowerCase() === previousName
    );
    return nameMatch?.productId ?? "";
  }

  function handlePreviousPrescriptionSelect(prescriptionId: string) {
    const previousPrescription = previousPrescriptions.find((option) => option.prescriptionId === prescriptionId);
    if (!previousPrescription) {
      updateDraft({ previousPrescriptionId: prescriptionId });
      return;
    }

    const productId = productIdFromPreviousPrescription(previousPrescription);
    const matchingProduct = productById(productId);
    const refillState = previousPrescriptionRefillState(previousPrescription);
    updateDraft({
      previousPrescriptionId: prescriptionId,
      prescriptionProductId: productId,
      prescriptionStrength: previousPrescription.strength ?? matchingProduct?.strength ?? "",
      prescriptionDosageForm: previousPrescription.dosageForm ?? matchingProduct?.dosageForm ?? "",
      prescriptionQuantity: previousPrescription.quantity ?? "",
      prescriptionRefills: refillState.prescriptionRefills,
      prescriptionPrn: refillState.prescriptionPrn,
      prescriptionExpirationDate: oneYearFromToday(),
      prescriptionDirections: previousPrescription.directions ?? draft.prescriptionDirections
    });
  }

  function clearPreviousPrescription() {
    updateDraft({
      previousPrescriptionId: "",
      prescriptionProductId: "",
      prescriptionStrength: "",
      prescriptionDosageForm: "",
      prescriptionQuantity: "",
      prescriptionRefills: "",
      prescriptionExpirationDate: oneYearFromToday(),
      prescriptionDirections: "",
      prescriptionPrn: false,
      prescriptionNotes: "",
      communicationMethod: "text"
    });
    setProductDetail(null);
    setProductDetailError(null);
    setIsProductDetailLoading(false);
    setIsQuantityMenuOpen(false);
  }

  function handleProductSelect(productId: string) {
    const product = productById(productId);
    updateDraft({
      prescriptionProductId: productId,
      prescriptionStrength: product?.strength ?? "",
      prescriptionDosageForm: product?.dosageForm ?? "",
      prescriptionQuantity: ""
    });
  }

  function clearProductSelection() {
    updateDraft({
      prescriptionProductId: "",
      prescriptionStrength: "",
      prescriptionDosageForm: "",
      prescriptionQuantity: ""
    });
    setProductDetail(null);
    setProductDetailError(null);
    setIsProductDetailLoading(false);
    setIsQuantityMenuOpen(false);
  }

  function adjustQuantity(delta: number) {
    const current = Number(draft.prescriptionQuantity || "0");
    const minimum = Number.isFinite(minimumOrderableQuantity) && minimumOrderableQuantity > 0
      ? minimumOrderableQuantity
      : 0;
    const next = Math.max(minimum, (Number.isFinite(current) ? current : 0) + delta);
    updateDraft({ prescriptionQuantity: formatWeightInput(next.toFixed(2)) });
  }

  function formatPrescriptionQuantityInput(value: string): string {
    const formatted = formatWeightInput(value);
    return decimalQuantityAllowed ? formatted : formatted.split(".")[0] ?? "";
  }

  return (
    <section className="order-step-panel">
      <div className="section-header">
        <div>
          <p className="eyebrow">Step 3 of 3</p>
          <h2 id="order-form-step-title">Prescription details</h2>
        </div>
        <button className="icon-button order-cancel-button" type="button" aria-label="Cancel prescription" onClick={onCancel}>
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <div className="order-field-group">
        <label>
          Historical prescription
          <div className="clearable-select-row">
            <SearchableSelect
              disabled={isPreviousPrescriptionsLoading || previousPrescriptions.length === 0}
              options={previousPrescriptions.map((prescription) => ({
                value: prescription.prescriptionId,
                label: prescription.label,
                searchText: previousPrescriptionSearchText(prescription)
              }))}
              placeholder={
                isPreviousPrescriptionsLoading
                  ? "Loading historical prescriptions..."
                  : previousPrescriptions.length
                    ? "Search historical prescriptions..."
                    : "No historical prescriptions found"
              }
              value={draft.previousPrescriptionId}
              valueLabel={selectedPreviousPrescription?.label ?? ""}
              onChange={handlePreviousPrescriptionSelect}
            />
            <button
              className="icon-button clear-select-button"
              type="button"
              aria-label="Clear selected historical prescription"
              disabled={!draft.previousPrescriptionId}
              onClick={clearPreviousPrescription}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </label>
        {previousPrescriptionsError ? (
          <div className="context-status warning compact" role="alert">
            <AlertCircle size={16} aria-hidden="true" />
            {previousPrescriptionsError}
          </div>
        ) : null}
        {productOptionsError ? (
          <div className="context-status warning compact" role="alert">
            <AlertCircle size={16} aria-hidden="true" />
            <span>We could not load the formulary. Try again before submitting.</span>
            <button className="inline-link-button" type="button" onClick={onRetryProductOptions}>
              Try again
            </button>
          </div>
        ) : null}
        <label>
          Formulary product *
          <div className="clearable-select-row">
            <SearchableSelect
              disabled={isProductOptionsLoading || !prefill || prefill.productOptions.length === 0}
              invalid={hasSubmitError("prescription.productId")}
              options={(prefill?.productOptions ?? []).map((product) => ({
                value: product.productId,
                label: product.label,
                searchText: `${product.drugName} ${product.strength ?? ""} ${product.dosageForm ?? ""}`
              }))}
              placeholder={
                isProductOptionsLoading
                  ? "Loading formulary..."
                  : prefill?.productOptions.length
                    ? "Search formulary..."
                    : "Formulary unavailable"
              }
              value={draft.prescriptionProductId}
              valueLabel={selectedProduct?.label ?? ""}
              onChange={handleProductSelect}
            />
            <button
              className="icon-button clear-select-button"
              type="button"
              aria-label="Clear selected formulary product"
              disabled={!draft.prescriptionProductId}
              onClick={clearProductSelection}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </label>
        <div className="order-field-grid">
          <label>
            Strength
            <input
              {...passwordManagerIgnoreAttributes}
              name="cppp-prescription-strength"
              value={draft.prescriptionStrength}
              onChange={(event) => updateDraft({ prescriptionStrength: event.target.value })}
            />
          </label>
          <label>
            Dosage form
            <input
              {...passwordManagerIgnoreAttributes}
              name="cppp-prescription-dosage-form"
              value={draft.prescriptionDosageForm}
              onChange={(event) => updateDraft({ prescriptionDosageForm: event.target.value })}
            />
          </label>
          <label>
            Quantity *
            <div
              className="quantity-stepper quantity-combobox"
              onBlur={() => window.setTimeout(() => setIsQuantityMenuOpen(false), 120)}
            >
              <input
                {...passwordManagerIgnoreAttributes}
                name="cppp-prescription-quantity"
                aria-invalid={hasSubmitError("prescription.quantity")}
                className={hasSubmitError("prescription.quantity") ? "field-invalid" : undefined}
                inputMode="decimal"
                value={draft.prescriptionQuantity}
                onChange={(event) => {
                  updateDraft({ prescriptionQuantity: formatPrescriptionQuantityInput(event.target.value) });
                  setIsQuantityMenuOpen(Boolean(selectableQuantityOptions.length));
                }}
                onFocus={() => setIsQuantityMenuOpen(Boolean(selectableQuantityOptions.length))}
              />
              {selectableQuantityOptions.length ? (
                <button
                  className="quantity-menu-toggle"
                  type="button"
                  aria-label="Show quantity options"
                  aria-expanded={isQuantityMenuOpen}
                  onClick={() => setIsQuantityMenuOpen((current) => !current)}
                >
                  <ChevronDown size={15} aria-hidden="true" />
                </button>
              ) : null}
              <div className="quantity-stepper-buttons" aria-label="Quantity controls">
                <button type="button" aria-label="Increase quantity" onClick={() => adjustQuantity(1)}>
                  <ChevronUp size={15} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  disabled={!draft.prescriptionQuantity || Number(draft.prescriptionQuantity) <= Math.max(0, Number.isFinite(minimumOrderableQuantity) ? minimumOrderableQuantity : 0)}
                  onClick={() => adjustQuantity(-1)}
                >
                  <ChevronDown size={15} aria-hidden="true" />
                </button>
              </div>
              {isQuantityMenuOpen && selectableQuantityOptions.length ? (
                <div className="quantity-options-menu" role="listbox" aria-label="Quantity options">
                  {selectableQuantityOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      role="option"
                      aria-selected={draft.prescriptionQuantity === option.value}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        updateDraft({ prescriptionQuantity: formatPrescriptionQuantityInput(option.value) });
                        setIsQuantityMenuOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </label>
          {pricingToShow ? (
            <dl className="product-detail-strip pricing-highlight span-2">
              <div>
                <dt>Estimated price</dt>
                <dd>${pricingToShow.estimatedPrice}</dd>
              </div>
              {pricingToShow.unitPrice ? (
                <div>
                  <dt>Unit price</dt>
                  <dd>${pricingToShow.unitPrice}</dd>
                </div>
              ) : null}
            </dl>
          ) : null}
          {isProductDetailLoading ? (
            <div className="context-status loading compact span-2" role="status">
              <LoaderCircle className="spin" size={16} aria-hidden="true" />
              Loading product pricing...
            </div>
          ) : null}
          {quantityValidation && !quantityValidation.ok ? (
            <div className="context-status warning compact span-2" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              {quantityValidation.message}
            </div>
          ) : null}
          {pricingWarning ? (
            <div className="context-status warning compact span-2" role="status">
              <AlertCircle size={16} aria-hidden="true" />
              {pricingWarning}
            </div>
          ) : null}
          {productDetailError ? (
            <div className="form-alert order-step-alert span-2" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              {productDetailError}
            </div>
          ) : null}
          <label>
            Number of refills
            <input
              {...passwordManagerIgnoreAttributes}
              name="cppp-prescription-refills"
              aria-invalid={hasSubmitError("prescription.refills")}
              className={hasSubmitError("prescription.refills") ? "field-invalid" : undefined}
              inputMode="numeric"
              disabled={draft.prescriptionPrn}
              value={draft.prescriptionRefills}
              onChange={(event) => updateDraft({ prescriptionRefills: event.target.value.replace(/\D/g, "").slice(0, 2) })}
            />
          </label>
          <label>
            Expires *
            <input
              {...passwordManagerIgnoreAttributes}
              name="cppp-prescription-expiration-date"
              aria-invalid={hasSubmitError("prescription.expirationDate")}
              className={hasSubmitError("prescription.expirationDate") ? "field-invalid" : undefined}
              type="date"
              value={draft.prescriptionExpirationDate}
              onChange={(event) => updateDraft({ prescriptionExpirationDate: event.target.value })}
            />
          </label>
          <label className="checkbox-row prescription-prn-row">
            <input
              type="checkbox"
              checked={draft.prescriptionPrn}
              onChange={(event) =>
                updateDraft({
                  prescriptionPrn: event.target.checked,
                  prescriptionRefills: event.target.checked ? "" : draft.prescriptionRefills
                })
              }
            />
            PRN
          </label>
          <label className="span-2">
            Directions for use *
            <textarea
              {...passwordManagerIgnoreAttributes}
              name="cppp-prescription-directions"
              aria-invalid={hasSubmitError("prescription.directions")}
              className={hasSubmitError("prescription.directions") ? "field-invalid" : undefined}
              value={draft.prescriptionDirections}
              onChange={(event) => updateDraft({ prescriptionDirections: event.target.value })}
            />
          </label>
          <div className="span-2 communication-preferences">
            <p>
              Text is the default communication method for this order. If the owner prefers another method, select the preferred option below.
            </p>
            <div className="checkbox-group">
              <label className="checkbox-row">
                <input
                  type="radio"
                  name="communicationMethod"
                  checked={draft.communicationMethod === "text"}
                  onChange={() => updateDraft({ communicationMethod: "text" })}
                />
                Text
              </label>
              <label className="checkbox-row">
                <input
                  type="radio"
                  name="communicationMethod"
                  checked={draft.communicationMethod === "phone"}
                  onChange={() => updateDraft({ communicationMethod: "phone" })}
                />
                Phone
              </label>
              <label className="checkbox-row">
                <input
                  type="radio"
                  name="communicationMethod"
                  checked={draft.communicationMethod === "email"}
                  onChange={() => updateDraft({ communicationMethod: "email" })}
                />
                Email
              </label>
            </div>
          </div>
          <label className="span-2">
            Notes
            <textarea
              {...passwordManagerIgnoreAttributes}
              name="cppp-prescription-notes"
              value={draft.prescriptionNotes}
              onChange={(event) => updateDraft({ prescriptionNotes: event.target.value })}
            />
          </label>
          {hasSavedSignature ? (
            <div className="context-status success compact span-2" role="status">
              <Check size={16} aria-hidden="true" />
              Saved prescriber signature will be used for this prescription.
            </div>
          ) : (
            <div className={hasSignatureSubmitError ? "order-signature-required span-2 field-invalid-group" : "order-signature-required span-2"}>
              <div className="context-status warning compact" role="alert">
                <AlertCircle size={16} aria-hidden="true" />
                No saved signature is on file. Draw a signature below before submitting.
              </div>
              <div className="signature-surface">
                <p className="signature-surface-label">Required signature</p>
                <div className={hasSignatureSubmitError ? "signature-pad-shell order-signature-pad-shell field-invalid" : "signature-pad-shell order-signature-pad-shell"}>
                  {!draft.orderSignatureBase64 ? <span className="signature-pad-placeholder">Draw signature here</span> : null}
                  <canvas
                    ref={signatureCanvasRef}
                    className="signature-pad"
                    aria-label="Draw required prescriber signature"
                    onPointerDown={startSignature}
                    onPointerMove={drawSignature}
                    onPointerUp={stopSignature}
                    onPointerCancel={stopSignature}
                    onPointerLeave={stopSignature}
                  />
                </div>
              </div>
              <button className="secondary-button compact-button" type="button" disabled={!draft.orderSignatureBase64} onClick={clearOrderSignaturePad}>
                Clear signature
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function SearchableSelect({
  id,
  describedBy,
  disabled = false,
  invalid = false,
  onChange,
  options,
  placeholder,
  value,
  valueLabel
}: {
  id?: string;
  describedBy?: string;
  disabled?: boolean;
  invalid?: boolean;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; searchText?: string }>;
  placeholder: string;
  value: string;
  valueLabel: string;
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [opensUp, setOpensUp] = useState(false);
  const controlRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = useMemo(
    () =>
      options
        .filter((option) => {
          if (!normalizedQuery) return true;
          return `${option.label} ${option.searchText ?? ""}`.toLowerCase().includes(normalizedQuery);
        })
        .slice(0, 80),
    [normalizedQuery, options]
  );

  function displayValue() {
    if (isOpen) return query;
    return valueLabel;
  }

  useEffect(() => {
    setQuery("");
    setIsOpen(false);
  }, [value]);

  function openPicker() {
    if (disabled) return;
    const rect = controlRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpensUp(spaceBelow < 220 && spaceAbove > spaceBelow);
    }
    setQuery("");
    setIsOpen(true);
  }

  function selectOption(optionValue: string) {
    setQuery("");
    setIsOpen(false);
    onChange(optionValue);
    window.requestAnimationFrame(() => {
      setIsOpen(false);
      inputRef.current?.blur();
    });
  }

  return (
    <div className={`${opensUp ? "searchable-select open-up" : "searchable-select"}${invalid ? " field-invalid-group" : ""}`}>
      <div className="searchable-select-control" ref={controlRef}>
        <input
          ref={inputRef}
          id={id}
          aria-describedby={describedBy}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-invalid={invalid}
          className={invalid ? "field-invalid" : undefined}
          autoComplete="off"
          data-1p-ignore="true"
          data-op-ignore="true"
          data-lpignore="true"
          data-bwignore="true"
          data-form-type="other"
          spellCheck={false}
          name={id ? `cppp-order-select-${id.replace(/email|phone|address|zip|postal|owner|patient/gi, "field")}` : undefined}
          disabled={disabled}
          placeholder={placeholder}
          value={displayValue()}
          onBlur={() => window.setTimeout(() => setIsOpen(false), 120)}
          onMouseDown={() => {
            if (!disabled) setIsOpen(true);
          }}
          onClick={openPicker}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onFocus={openPicker}
        />
        <button
          className="searchable-select-toggle"
          type="button"
          aria-label={isOpen ? "Close options" : "Open options"}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (disabled) return;
            if (isOpen) {
              setIsOpen(false);
              return;
            }
            openPicker();
          }}
        >
          <ChevronDown size={18} aria-hidden="true" />
        </button>
      </div>
      {isOpen && !disabled ? (
        <div className="searchable-select-menu" role="listbox">
          {visibleOptions.length ? (
            visibleOptions.map((option) => (
              <button
                className={option.value === value ? "searchable-select-option selected" : "searchable-select-option"}
                type="button"
                role="option"
                aria-selected={option.value === value}
                key={option.value}
                onPointerDown={(event) => {
                  event.preventDefault();
                  selectOption(option.value);
                }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.preventDefault();
                  if (event.detail === 0) {
                    selectOption(option.value);
                  }
                }}
              >
                {option.label}
              </button>
            ))
          ) : (
            <p className="searchable-select-empty">No matches found.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function formatPhoneInput(value: string) {
  const rawDigits = value.replace(/\D/g, "");
  const digits = rawDigits.length === 11 && rawDigits.startsWith("1") ? rawDigits.slice(1, 11) : rawDigits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatWeightInput(value: string) {
  const cleaned = value.replace(/[^\d.]/g, "");
  const [whole = "", ...decimalParts] = cleaned.split(".");
  const decimals = decimalParts.join("").slice(0, 2);
  if (cleaned.includes(".")) return `${whole.slice(0, 4)}.${decimals}`;
  return whole.slice(0, 4);
}

function formatWeightForDisplay(value: string) {
  const cleaned = formatWeightInput(value);
  if (!cleaned || cleaned === ".") return "";
  const numeric = Number(cleaned);
  if (!Number.isFinite(numeric)) return "";
  return numeric.toFixed(2);
}

